// ════════════════════════════════════════════════════════════════════
// TERROR — THE TOME RESOLVER (extracted from Shatterfield.js)
// ────────────────────────────────────────────────────────────────────
// TERROR_REFEREE.md §6: "applyTomeEffect is 1,473 lines locked inside a
// React component... the root cause of the printed-text-versus-code
// class of bug... A real refactor: lift it to a module parameterised
// by {code, playerIdx, oppIdx, chooseTarget, notify} — Shatterfield
// injects the picker, the brain injects a scoring function." This file
// is that lift, done mechanically and behavior-neutrally: AT THE LIFT,
// every one of the functions below WAS byte-identical game logic to what
// shipped in Shatterfield.js, with exactly one change — component-scope
// closures (match, matchRef, me, opponent, showToast, setTargetPicker,
// setSgModal, updatePlayer, factionStatsRef, responseWindowRef, code,
// playerIdx, oppIdx) became an explicit `ctx` parameter instead of an
// implicit closure. Cross-calls between them pass `ctx` through.
//
// ⚠ IT IS NO LONGER BYTE-IDENTICAL AND SAYING SO MATTERS (2026-09-14).
// This header claimed byte-identity long after it stopped being true —
// R105's fare, R103's durations, R123's Tome ward, R125's Intangible
// Treasure, the Luxmoire corrections and the Radiant-Armor/Corruption-Jet
// split all landed HERE, by hand, after the lift. A header that states a
// property the file no longer has is a citation the source contradicts,
// and the next reader believes it. THIS FILE IS THE TEXT; Shatterfield.js
// is a set of one-line wrappers around it.
//
// ⭐ THE PARAMETER-ORDER LAW, written where the lift lives:
//   `ctx` GOES IN FRONT OF EVERY OPTIONAL PARAMETER.
// The board's wrappers are `(...args) => resolver_X(...args, ctx)` and
// callers omit optionals, so ctx arrives at the first position a caller
// did not fill. When `descendBeing` read
// `(targetPlayerIdx, slotIdx, isNecropolis = true, ctx)`, ctx landed in
// `isNecropolis`, the real ctx was undefined, and the function threw on
// EVERY ruin the player caused — for eighteen days, with every suite
// green, because a parameter ORDER is not source text and no scan can
// see it. tools/harness/extractTomeResolver.cjs now throws rather than
// emit that shape, and tools/cardText.selftest.mjs CALLS these functions
// instead of reading them.
//
// Shatterfield.js now calls these via thin wrappers that build `ctx`
// from its own live component state — see the "const ctx = {...}"
// block and the one-line wrappers that replaced each original
// definition. Nothing about WHAT each function does changed; only
// WHERE the state it needs comes from.
//
// Three functions needed no capture at all (getRequiredTarget,
// normPick, cascadeWieldedTreasures) and are imported directly by
// Shatterfield.js with no wrapper — they were always pure. Their call
// sites inside this file briefly carried a trailing `, ctx` the
// extraction appended by position; JavaScript dropped it, so it never
// broke anything and only made three pure helpers look ctx-bound. Gone.
//
// ⭐ ONE EXPORT WAS ADDED BY HAND, not by the lift: `ruinBeingAndSoul`.
// "Ruin 1 being and its Soul" had three hand-rolled copies here and a
// fourth in Shatterfield's ATV feed modal, and they had drifted on the
// C13 ward soak, on R68's Heretical Tome and on how many slot keys a
// ruined rift clears. It is one function now, and it is exported so the
// feed modal can come under applyTomeEffect's R123 ward wrapper instead
// of writing around it. See its own banner.
//
// getCard / stripSoulCard / ALL_CARDS_FLAT / animaFare / GM below are
// verbatim copies of Shatterfield.js's own module-scope versions
// (themselves pure, dependency-free) so this file needs nothing from
// Shatterfield.js itself — importing FROM Shatterfield.js would drag
// its JSX into this file's closure and make it unmirrorable by
// tools/harness/build.mjs, which is the whole point of this file
// existing. NPC.js carries its own independent copies of some of
// these already (stripSoulCard, getCard) — this is a third, not a new
// pattern; unifying all three is future work, not this pass's job.
//
// ⚠ GENERATED BY EXTRACTION, BUT THIS FILE IS THE SOURCE OF TRUTH NOW.
// Unlike tools/.build/*.mjs mirrors, this file is checked in and hand-
// editable going forward — the generation step only happened once, to
// get it out of Shatterfield.js correctly. Fix bugs here, not there.
// ════════════════════════════════════════════════════════════════════
import { ESCENDANTS, TOMES, IMPERA, UHLTIS, AFFINITIES, PROPHECIES, CURIOS, ESCLINGS } from "./cards";
import { baseId, resTally } from "./necroSeal";
import {
  activationFare, tomeWardScope, tomeWardBlocks, tomeIsIntangible,
  durationExpiryTurn, durationStillRunning, farePoolsFor, isLuxSearchable,
  PHASE, PHASES, phaseOf, phaseAllows, inAttackPhase,
  GRAVESHIFT_COST, LUX_SEARCH_COST, RESURRECT_COST, RESURRECT_LIMIT, STARTING_HAND, MAX_KHAO_ABS, grimSearchFare,
  RESURRECT_PER_TURN, STARTING_KHAO, MAX_KHAO_NAT, FIRST_KHAO_TURN,
  PRIMERAS, PRIMERA_COST, TOME_WAIT, DUEL_TIME_LIMIT, timeoutWinner,
  HERETICAL_TRAINING_COST, canPayVoluntary, STARTING_ANIMA, HAND_LIMIT,
  cooldownHalfTurns, deviantFare,
  isOpeningCycle, adversaryDamageInPayload, isOneVsOne,
} from "./rules";
import { buildAnimaLossUpdate, buildSelfDimUpdate, buildAnimaGainUpdate, getProphecyKhaoCost,
         applyProphetsToll, boardAfterToll, collectEmptiedSides, tollStamp } from "./prophecyEngine";
import { sealAnimaGain } from "./ruleGate";
import { SD } from "./theme";
import { withEffectiveAnima } from "./matchSeats";

// ── Verbatim copies of Shatterfield.js's own module-scope helpers ──
// (see header note above for why these are copied, not imported)
const ALL_CARDS_FLAT = [...ESCENDANTS, ...TOMES, ...IMPERA, ...UHLTIS, ...AFFINITIES, ...PROPHECIES, ...CURIOS, ...ESCLINGS];
const getCard = (id) => {
  if (!id) return null;
  return ALL_CARDS_FLAT.find(c => c.id === baseId(id)) || null;
};
// ⚠ A `function` DECLARATION ON PURPOSE (2026-09-15, one resolver).
// tools/harness/build.mjs instruments every `function name(` in this
// file's mirror so the balancer's transition referee can see that a
// Soul card was stripped LAWFULLY in a half-turn (X11 declines to blame
// a descent for an Impera a "ruin 1 Impera" Tome took). The day the
// brain started resolving through this file, this arrow was invisible to
// that probe and X11 fired on a legal board. Every soul-strip in this
// resolver goes through here — keep it that way.
function stripSoulCard(soul, soulId) {
  const card = getCard(soulId);
  const gone = [soulId];
  if (card?.type === "U'hlti") {
    (soul || []).forEach(id => { if (getCard(id)?.type === "Affinity") gone.push(id); });
  }
  return { soul: (soul || []).filter(id => !gone.includes(id)), gone };
}
const animaFare = (n) => `${n + 1}+ Anima`;
// Verbatim copy of Shatterfield.js's module-scope livePlayer (line
// ~496), rewritten to take ctx.matchRef explicitly instead of the
// module-level `_sealMatchRef` singleton -- `_sealMatchRef = matchRef`
// is set once in Shatterfield.js (see its own header comment there),
// so ctx.matchRef IS the same ref object; this is not a behavior
// change, just naming the alias instead of going through it.
const livePlayer = (idx, fallback, ctx) => ctx.matchRef?.current?.players?.[idx] || fallback;
// Grimoire Manuscript Palette — UI chrome only, verbatim from
// Shatterfield.js's own module-scope GM. Only used here for color
// arguments passed to ctx.showToast / ctx.setTargetPicker.
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  pageLight:     "#2e2410",
  pageBorder:    "#3d2e14",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  inkBrown:      "#8a6a30",
  inkRed:        "#8b2020",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  ruling:        "rgba(139,106,48,0.18)",
  font: "'Cinzel', 'Palatino Linotype', serif",
};

// ── R-MRF: mustBeRuinedFirst — priority-target enforcement ──
  //
  // Some cards force the adversary to destroy them before any other
  // attack is legal. Currently these cards use the flag:
  //
  //   • H-01 Quasar (escling slot)        — set by Impera p_h01_2
  //   • Khao D'uhmdumn Beb/Champion/Lord  — intrinsic to card identity
  //
  // CANON (caP'n, July 2026): Khao D'uhmdumn is a TAUNT WALL. "Every
  // single attack must first be directed at the D'uhmdumn, whether Lyt
  // attack, Tome, etc." It has Lyf (Beb 0 / Champion 1 / Lord 1) and IS
  // Lyt-attackable: Lyt >= its current Lyf ruins it to Necropolis;
  // Lyt < Lyf chips it. The Lord's nullifyFirstRuin soaks the FIRST hit
  // of ANY kind (tiny Lyt poke or board-wiping tome alike) — flag
  // consumed, nothing happens. Tome-ruining effects are also force-
  // targeted at it (see the `forced` blocks in applyTomeEffect).
  // See handleAttackOppTome for the Lyt path.
  //
  // The helper returns the first active priority target (or null) so
  // every attack path (Lyt, escling Lyt, Anima, tome-based being-ruin)
  // can enforce the same rule consistently.
  //
  // Returns: { kind: "escling"|"tome", slotIdx, id, name } | null
  //
  // SELF-DERIVING DESIGN (June 2026 fix): the tome-side requirement is now
  // computed from CARD IDENTITY, not a persisted `mustBeRuinedFirst` flag.
  // The D'uhmdumn family ALWAYS demands ruin-first while face-up on the
  // field — that's intrinsic to the card text, not match state. Relying on
  // a stored boolean was the root of Bug #4: the flag was set in some place-
  // ment paths (handlePlayTome, radial flip) but NOT others (handleInterrupt-
  // FromField), and the Lord's nullifyFirstRuin was only written via a stale
  // me.field scan that could silently miss. Deriving from the id removes ALL
  // of those race windows at once. A face-down tome is hidden, so it never
  // blocks. Quasar stays flag-based because its requirement is ACTIVATED
  // state (set by Impera p_h01_2), not intrinsic to the escendant.
  const TOME_RUIN_FIRST_IDS = new Set([
    "tome_khao_duhmdumn_beb",
    "tome_khao_duhmdumn_champion",
    "tome_khao_duhmdumn_lord",
  ]);

// ── THE WIELDED TREASURES, AND THE BOND (2026-09-15, one resolver) ──
// "This Treasure belongs to 1 being." Three Tomes bond to a wielder when
// they activate face-up; until today the bond was made OUTSIDE the
// resolver — twice in Shatterfield (the play-from-hand path and the flip
// path, each with its own picker) and once in NPC.execBond (highest Lyt,
// unclaimed). Three copies of one bond. It is one function now, opened by
// applyTomeEffect for every activation of one of these ids, so the human
// and the brain bond through the same picker and the same write.
export const WIELDER_REQUIRED_TREASURES = new Set([
  "tome_lightning_blade",
  "tome_sword_of_shadows",
  "tome_gauntlets_azka",
]);
export const needsWielder = (card) => !!card && WIELDER_REQUIRED_TREASURES.has(card.id);
export const bondWielderIfNeeded = async (card, ctx) => {
  if (!needsWielder(card)) return false;
  const meL = livePlayer(ctx.playerIdx, ctx.me, ctx);
  const tomes = meL?.field?.tomes || [];
  let slotIdx = -1;
  for (let i = 0; i < 3; i++) { const t = tomes[i]; if (t && typeof t === "object" && t.id === card.id && !t.faceDown) { slotIdx = i; break; } }
  if (slotIdx < 0) return false;                                       // ruined or face-down — nothing to bond
  const held = tomes[slotIdx].wieldedBy;
  if (held !== null && held !== undefined && meL?.field?.esclings?.[held]?.escendant) return false;   // already bonded to a standing being
  const alive = (meL?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
  if (alive.length === 0) { ctx.showToast(`${card.name} needs a wielder — no beings available.`, SD.red); return false; }
  ctx.setTargetPicker({
    title: `◆ ${card.name} — Choose a wielder`,
    options: alive.map(({ s, i }) => ({ id: String(i), label: `Slot ${i + 1}: ${getCard(s.escendant)?.name}`, slotIdx: i })),
    onPick: async (opt) => {
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { [`field/tomes/${slotIdx}/wieldedBy`]: opt.slotIdx });
      ctx.showToast(`✦ ${card.name} bonded to ${getCard(alive.find(ab => ab.i === opt.slotIdx)?.s.escendant)?.name}!`, SD.gold);
      ctx.setTargetPicker(null);
    },
  });
  return true;
};

export const fireShieldTome = async (card, scope, ctx) => {
    const stampBeing = async (slotIdx) => {
      const meN = ctx.matchRef.current?.players?.[ctx.playerIdx] || ctx.me;
      const f = JSON.parse(JSON.stringify(meN?.field || {}));
      const s = f.esclings?.[slotIdx];
      if (!s?.escendant || s.nullifyNextAttack) return false;
      f.esclings[slotIdx] = { ...s, nullifyNextAttack: true };
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast(`◈ ${card.name} — ${getCard(s.escendant)?.name} will nullify the next attack against it!`, GM.inkGold);
      return true;
    };
    const stampTome = async (tomeIdx) => {
      const meN = ctx.matchRef.current?.players?.[ctx.playerIdx] || ctx.me;
      const f = JSON.parse(JSON.stringify(meN?.field || {}));
      const t = f.tomes?.[tomeIdx];
      if (!t || typeof t !== "object" || !t.id || t.nullifyFirstRuin) return false;
      f.tomes[tomeIdx] = { ...t, nullifyFirstRuin: true };
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast(`◈ ${card.name} — ${getCard(t.id)?.name} will soak the next hit against it!`, GM.inkGold);
      return true;
    };
    // In-window auto-target — the card under attack is the only pick
    // that makes sense; stamping completes BEFORE the window resolves,
    // so the declarer's fresh re-read sees the ward. This is the race
    // a picker could never win.
    // R136 — anyone may answer, so a window can be MINE without the
    // swing being aimed at MY board. `aimedAtMe` is stamped on the action
    // by the response lane; absent (1v1, the NPC lane) means it is mine.
    const rwActRaw = ctx.responseWindowRef.current?.action;
    const rwAct = rwActRaw && rwActRaw.aimedAtMe === false ? null : rwActRaw;
    if (rwAct?.kind === "LYT_ATTACK" && rwAct.targetIdx !== undefined && rwAct.targetIdx !== null) {
      if (await stampBeing(rwAct.targetIdx)) return;
    }
    if (scope === "cards" && rwAct?.kind === "LYT_ATTACK_TOME") {
      let tIdx = (rwAct.targetIdx !== undefined && rwAct.targetIdx !== null) ? rwAct.targetIdx : null;
      if (tIdx === null) {
        const reqMine = getRequiredTarget(ctx.matchRef.current?.players?.[ctx.playerIdx]?.field || ctx.me?.field);
        if (reqMine?.kind === "tome") tIdx = reqMine.slotIdx;
      }
      if (tIdx !== null && (await stampTome(tIdx))) return;
    }
    // Proactive play — pick the ward's home
    const meN = ctx.matchRef.current?.players?.[ctx.playerIdx] || ctx.me;
    const beings = (meN?.field?.esclings || []).map((s, i) => ({ s, i }))
      .filter(({ s }) => s?.escendant && !s.fallen && !s.nullifyNextAttack);
    const wardTomes = scope === "cards"
      ? (meN?.field?.tomes || []).map((t, i) => ({ t, i }))
          .filter(({ t }) => t && typeof t === "object" && t.id && !t.nullifyFirstRuin)
      : [];
    const options = [
      ...beings.map(({ s, i }) => ({ id: s.escendant, label: `Being: ${getCard(s.escendant)?.name}`, slotIdx: i, kind: "being" })),
      ...wardTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}${t.faceDown ? " (face-down)" : ""}`, tomeIdx: i, kind: "tome" })),
    ];
    if (options.length === 0) { ctx.showToast("Nothing standing to ward.", GM.vellumFaint); return; }
    ctx.setTargetPicker({
      title: `◈ ${card.name} — Choose what holds the ward`,
      options,
      onPick: async (opt) => {
        if (opt.kind === "being") await stampBeing(opt.slotIdx);
        else await stampTome(opt.tomeIdx);
        ctx.setTargetPicker(null);
      },
    });
  };

// Normalize TargetPickerModal onPick result — always returns array of card IDs
  export const normPick = (chosen) => {
    if (Array.isArray(chosen)) return chosen;
    if (typeof chosen === "string") return [chosen];
    if (chosen?.id) return [chosen.id];
    return [];
  };

export const tillRefusesSelfDim = (cost, label, ctx) => {
    if (cost <= 0) return false;
    const live = ctx.matchRef.current?.players?.[ctx.playerIdx]?.anima ?? ctx.me?.anima ?? STARTING_ANIMA;
    if (canPayVoluntary(live, cost)) return false;
    ctx.showToast(
      `◆ THE TILL LAW — ${label || "this"} dims ${cost} of your OWN Anima and you hold ${live}. You may not end yourself.`,
      SD.red,
    );
    return true;
  };

export const consumeSGAutoWin = async (ctx) => {
    const liveField = ctx.matchRef.current?.players?.[ctx.playerIdx]?.field || ctx.me?.field;
    if (!liveField?.nextSGAutoWin) return false;
    const f = JSON.parse(JSON.stringify(liveField));
    f.nextSGAutoWin = false;
    await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
    ctx.showToast("✦ Casino Royale's fortune — this Shadow Gambling is an automatic win!", SD.gold);
    return true;
  };

export const recordShadowGamblingOutcome = async (won, ctx) => {
    const current = ctx.me?.shadowGamblingStreak || 0;
    const next = won ? current + 1 : 0;
    const updates = {};
    if (next !== current) updates.shadowGamblingStreak = next;
    // Resolve any pending bet from Underdog's Howl
    const pendingBet = ctx.me?.field?.pendingBet;
    if (pendingBet) {
      const newField = JSON.parse(JSON.stringify(ctx.me?.field || {}));
      newField.pendingBet = null;
      updates.field = newField;
      const betCard = getCard(pendingBet);
      // ⭐ THE STAKE MOVES, OR NOTHING MOVES. Both legs used to write the
      // DESTINATION unconditionally and the Grimoire decrement only `if
      // (newG[pendingBet] > 0)` — so a bet whose card had since left the
      // pile (drawn, milled, or a stake that was never a real id at all)
      // MINTED a copy into the hand or the Necropolis. Multiset
      // conservation, broken in the direction no length check can see: the
      // pile's size did not fall, so nothing noticed it rise elsewhere.
      // Now the decrement is computed first and the destination write only
      // happens if a real copy actually came out of the Grimoire.
      const liveBet = livePlayer(ctx.playerIdx, ctx.me, ctx);
      const newG = { ...(liveBet?.grimoire || {}) };
      const held = (newG[pendingBet] || 0) > 0;
      if (held) {
        if (newG[pendingBet] > 1) newG[pendingBet] -= 1; else delete newG[pendingBet];
        updates.grimoire = newG;
      }
      if (!held) {
        // The bet is void: the card is not in the Grimoire to be won or lost.
        // pendingBet is still cleared above, so the stake cannot haunt the
        // next gamble.
        ctx.showToast(`◇ Underdog's Howl — ${betCard?.name || "the staked card"} is no longer in your Grimoire; the bet is void.`, GM.vellumFaint);
      } else if (won) {
        updates.hand = [...(liveBet?.hand || []), pendingBet];
        ctx.showToast(`✦ Underdog's Howl — Won the bet! ${betCard?.name} added to hand.`, SD.gold);
      } else {
        updates.necropolis = [...(liveBet?.necropolis || []), pendingBet];
        ctx.showToast(`◇ Underdog's Howl — Lost the bet. ${betCard?.name} sent to Necropolis.`, GM.vellumFaint);
      }
    }
    if (Object.keys(updates).length > 0) {
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, updates);
    }
    if (won && next >= 2) {
      ctx.showToast(`◆ Shadow Gambling streak: ${next}`, SD.gold);
    } else if (!won && current >= 2) {
      ctx.showToast(`◇ Shadow Gambling streak broken (was ${current}).`, GM.vellumFaint);
    }
  };

export const triggerHopeIfTimeEntry = (enteringCard, updatedMyField, currentAnima, ctx) => {
    if (!enteringCard || enteringCard.school !== "Time") return currentAnima;
    const iHaveHope = (updatedMyField?.esclings || []).some(s =>
      (s?.soul || []).some(id => id === "p_dg0_1")
    );
    if (!iHaveHope) return currentAnima;
    ctx.showToast(`◆ Hope — ${enteringCard.name} entered combat! +1 Anima.`, GM.inkGold);
    // ⚠ SEALED, like every other gain. This was the one raw `+ 1` left in
    // the file: five call sites write its result straight to `anima`, so a
    // seat on 50 gained a 51st and R32's ceiling — which every other gain
    // in the game passes through — was simply not asked.
    return sealAnimaGain(currentAnima ?? 0, 1, "Hope");
  };

export const fireAkulaListenerOnRuin = async (ctx) => {
    // Owner of Akula (if any) gains +1 Anima
    const myHasAkula = (ctx.me?.field?.tomes || []).some(t => t?.id === "tome_necklace_akula" && !t?.faceDown);
    const oppHasAkula = (ctx.opponent?.field?.tomes || []).some(t => t?.id === "tome_necklace_akula" && !t?.faceDown);
    if (myHasAkula) {
      const na = sealAnimaGain((ctx.me?.anima || STARTING_ANIMA), 1);
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { anima: na, ...buildAnimaGainUpdate(ctx.me, 1) });
      ctx.showToast(`◈ Necklace of Akula — +1 Anima (now ${na})!`, GM.inkGold);
    }
    if (oppHasAkula) {
      const na = sealAnimaGain((ctx.opponent?.anima || STARTING_ANIMA), 1);
      await ctx.updatePlayer(ctx.code, ctx.oppIdx, { anima: na, ...buildAnimaGainUpdate(ctx.opponent, 1) });
    }
  };

export const searchAndAdd = async (source, filter, count, label, ctx) => {
    // source: "grimoire" | "necropolis" | "luxmoire"
    // ⚠ READ THE LIVE SEAT, NOT THE ACTIVATION SNAPSHOT (2026-09-15, found
    // by the one-resolver differential on its first run). This read
    // `ctx.me` — the seat as it was when the Tome was activated — so a card
    // that tutors TWICE in one activation (Light Casting Shadow: "1 Impera
    // from your Luxmoire AND 1 U'hlti from your Grimoire") computed its
    // second hand from the pre-first-tutor snapshot and the second write
    // overwrote the first: the Impera arrived, then vanished. Same on the
    // human's board, every time, since the card was written.
    const seatNow = () => livePlayer(ctx.playerIdx, ctx.me, ctx);
    const me0 = seatNow();
    const pool = source === "necropolis"
      ? (me0?.necropolis || []).filter(id => filter(getCard(id)))
      : Object.keys(source === "grimoire" ? (me0?.grimoire || {}) : (me0?.luxmoire || {})).filter(id => filter(getCard(id)));
    if (pool.length === 0) { ctx.showToast(`No ${label} available.`, GM.vellumFaint); return false; }
    const land = async (chosenArr) => {
      const meL = seatNow();
      const newHand = [...(meL?.hand || []), ...chosenArr];
      if (source === "necropolis") {
        // ── GRIM/LUX FIX (July 2026): remove ONE instance per pick.
        // The old `.filter(!includes)` deleted EVERY copy of a picked
        // id — a Necropolis holding duplicates lost them all.
        const newNec = [...(meL?.necropolis || [])];
        chosenArr.forEach(id => { const ni = newNec.indexOf(id); if (ni !== -1) newNec.splice(ni, 1); });
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: newHand, necropolis: newNec });
      } else {
        // ── GRIM/LUX FIX (July 2026): tutors must DEPLETE the pool ──
        // Morning Star / Binary Star / etc. were adding the card to hand
        // while leaving the Grimoire copy in place — every tutor
        // duplicated a card. Decrement one copy per tutored id.
        const poolKey = source === "grimoire" ? "grimoire" : "luxmoire";
        const newPool = { ...(meL?.[poolKey] || {}) };
        chosenArr.forEach(id => { if (newPool[id] > 1) newPool[id]--; else delete newPool[id]; });
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: newHand, [poolKey]: newPool });
      }
      chosenArr.forEach(id => ctx.showToast(`✦ ${getCard(id)?.name} added!`, GM.inkGold));
    };
    if (pool.length <= count) {
      await land(pool);
    } else {
      // Show picker modal
      ctx.setTargetPicker({
        title: `Choose ${count} — ${label}`,
        cards: pool,
        count,
        onPick: async (chosen) => {
          // TargetPickerModal passes item directly — normalize to array
          const chosenArr = Array.isArray(chosen) ? chosen : [typeof chosen === "string" ? chosen : chosen?.id];
          await land(chosenArr);
          ctx.setTargetPicker(null);
        },
      });
    }
    return true;
  };

// ⚠ PARAMETER ORDER IS LOAD-BEARING (fixed 2026-09-14). `ctx` MUST sit in
// front of any optional parameter. The board's wrapper is
// `(...args) => resolver_descendBeing(...args, ctx)` and every caller in the
// game passes two positional args, so ctx arrives THIRD. When ctx sat fourth
// behind `isNecropolis = true`, ctx landed in isNecropolis, the real ctx was
// undefined, and this function threw `Cannot read properties of undefined
// (reading 'playerIdx')` on EVERY ruin the player caused — the brain's ruins
// still worked, because they go through NPC.descendPlayerSlot. One-sided duel.
// tools/cardText.selftest.mjs now executes a real three-arg call.
export const descendBeing = async (targetPlayerIdx, slotIdx, ctx, isNecropolis = true) => {
    // LIVE READ (C12) — a Lyt Attack writes damage to this same field a
    // moment before we get here. Cloning the render closure would read the
    // pre-damage board, and worse, a Soul written after the last paint
    // reads EMPTY — which sends the being's whole Soul to the Necropolis
    // instead of descending it. That was the "soul destroying bug."
    const pData = livePlayer(targetPlayerIdx, targetPlayerIdx === ctx.playerIdx ? ctx.me : ctx.opponent, ctx);
    const field = JSON.parse(JSON.stringify(pData?.field || {}));
    const slot = field.esclings?.[slotIdx];
    if (!slot?.escendant) return;
    const topCard = getCard(slot.escendant);
    if (!topCard) return;
    const necropolis = [...(pData?.necropolis || [])];

    // Cards being ruined — top escendant + its level-specific soul cards
    if (isNecropolis) necropolis.push(slot.escendant);

    // Find previous level escendant in soul (soul is array of card IDs)
    const soul = slot.soul || [];
    const prevLevelId = soul.find(id => {
      const c = getCard(id);
      return c?.type === "Escendant" && c?.level === (topCard.level - 1);
    });

    if (prevLevelId && topCard.level > 1) {
      // ── DESCENT — canon (caP'n, 13 Sept 2026; OVERTURNS July 2026) ──
      //   • Lower-level Escendants stay in soul.
      //   • ALL Impera STAY. The July 2026 "level-bound" reading — that
      //     the ruined level's Impera ruined with it — is DEAD. RULEZ_V2
      //     :541 "All Impera, U'hltis and Affinities remain attached —
      //     Soul attachments are never level-specific" and :563 "An
      //     Impera trained onto a Level 1 is still there at Level 3 and
      //     still there when the being falls back to Level 1."
      //     An Impera is a lesson a being cannot unlearn.
      //   • U'hltis stay — permanent unless specifically ruined.
      //   • Affinities stay — permanent like U'hltis.
      // The ruined top-level Escendant was already pushed to Necropolis
      // above via `necropolis.push(slot.escendant)`.
      const keptSoul = soul.filter(id => {
        const c = getCard(id);
        if (!c) return false;
        if (c.type === "Escendant") return true; // any lower-level Escendant stays
        if (c.type === "Impera") return true;    // R51/R52 — every Impera stays, at every level
        if (c.type === "U'hlti" || c.type === "Affinity") return true;
        return false;
      });
      const ruinedSoul = soul.filter(id => !keptSoul.includes(id));
      ruinedSoul.forEach(id => necropolis.push(id));

      // 🔥 R68 — THE HERETICAL TOME DOES NOT RIDE DOWN. The rite
      // requires a Level 3 and this slot is about to stop being one, so
      // the bond breaks with the body it was bonded to and the Tome is
      // ruined into the Necropolis like anything else that just died.
      // It is NOT in `soul`, so nothing above this line touched it and
      // nothing below would have cleared it — `{...slot}` would have
      // carried it down onto a Champion, where it is illegal, and left
      // it there permanently.
      if (slot.heretical) necropolis.push(slot.heretical);

      const prevCard = getCard(prevLevelId);
      field.esclings[slotIdx] = {
        ...slot,
        heretical: null,
        escendant: prevLevelId,
        currentLyf: prevCard?.lyf ?? null,
        soul: keptSoul.filter(id => id !== prevLevelId),
        attackedThisTurn: false,
        intangible: false,
        // One-shot / chosen-target flags belonged to the RUINED level —
        // they do not ride down (Quasar's pick was the visible being;
        // nullify/reflect are next-attack one-shots consumed by descent).
        mustBeRuinedFirst: false,
        nullifyNextAttack: false,
        reflectNextAttack: false,
        // ── Fallen (one-turn state, canon July 2026) ──
        // The descended being is Fallen for the remainder of THIS turn:
        //   • Cannot be Lyt-attacked (R25)
        //   • BYPASSABLE to Anima immediately (R26) — it does NOT shield
        // fallenTurn snapshots the match turn at descent (diagnostic);
        // the flag clears at the owner's TURN START (R26) — see the
        // FALLEN HALF-TURN blocks in the listener and in handleEndTurn.
        // (The old note here said "end-of-turn reset", which the code has
        // never done: Fallen lasts exactly the adversary's half-turn.)
        fallen: true,
        fallenTurn: ctx.match?.turn || 0,
        // Nullification rides with the surviving souls; departed entries drop.
        nullifiedSouls: (slot.nullifiedSouls || []).filter(id => keptSoul.includes(id)),
        // ⭐ R105b/R122 · AND SO DOES THE CADENCE STAMP. `soulUsed` is a
        // PER-CARD map of the half-turn each soul card last fired, and the
        // spread above carried the WHOLE map down — including entries for
        // soul cards that just went to the Necropolis. Harmless while the
        // card is gone, and a silent lie the moment the same card id comes
        // back onto this slot by resurrection or a fresh training: it would
        // arrive already on cooldown, for a firing a DIFFERENT copy did,
        // possibly many Turns ago. Filtered the same way nullifiedSouls is,
        // one line above, because it is the same question about the same
        // departed ids.
        soulUsed: Object.fromEntries(
          Object.entries(slot.soulUsed || {}).filter(([sid]) => keptSoul.includes(sid))),
      };
    } else {
      // No lower level — fully ruin, soul all goes to Necropolis
      soul.forEach(id => necropolis.push(id));
      // 🔥 R68 — and the Heretical Tome with it. See the descent branch.
      if (slot.heretical) necropolis.push(slot.heretical);
      field.esclings[slotIdx] = {
        ...slot, escendant: null, currentLyf: null, soul: [], soulUsed: {}, heretical: null, fallen: false,
        attackedThisTurn: false, intangible: false, nullifiedSouls: [],
      };
      // ── Wave 1: cascade wielded Treasures ─────────────────────
      // When a being is fully ruined back to Escling, every Treasure
      // bonded to that slot is ruined with it (per Treasure rule:
      // "Esclings are unaffected — the Treasure dies with the lineage").
      // We modify field.tomes in place and the single updatePlayer
      // below writes everything atomically.
      (field.tomes || []).forEach((t, i) => {
        if (t?.id && t?.wieldedBy === slotIdx) {
          necropolis.push(t.id);
          field.tomes[i] = null;
        }
      });
      await ctx.updatePlayer(targetPlayerIdx === ctx.playerIdx ? ctx.code : ctx.code, targetPlayerIdx, { field, necropolis });
      // ── Wave 2: Necklace of Akula listener — fires on full ruin only ──
      // Descent (L2→L1) doesn't count as "ruined" for Akula purposes;
      // only when the being is fully sent to Necropolis.
      await fireAkulaListenerOnRuin(ctx);
      return;
    }
    await ctx.updatePlayer(targetPlayerIdx === ctx.playerIdx ? ctx.code : ctx.code, targetPlayerIdx, { field, necropolis });
    // ── Wave 2: Necklace of Akula listener — fires on ruin events ──
    await fireAkulaListenerOnRuin(ctx);
  };

// ── Wave 1: reusable wielder-cascade helper ──────────────────
  // Takes a field + necropolis and mutates both in place:
  //   • Any Tome rift slot with wieldedBy === ruinedSlotIdx is cleared
  //   • That Tome's id is pushed onto necropolis
  // Intended for direct-clear ruin paths that don't go through
  // descendBeing (e.g. Necklace of Death trigger, Weeping Dagger, etc.).
  // Caller is responsible for writing field + necropolis via updatePlayer.
  export const cascadeWieldedTreasures = (field, necropolis, ruinedSlotIdx) => {
    if (!field?.tomes) return;
    field.tomes.forEach((t, i) => {
      if (t?.id && t?.wieldedBy === ruinedSlotIdx) {
        necropolis.push(t.id);
        field.tomes[i] = null;
      }
    });
  };

// ── ⭐ THE ONE FULL BEING-RUIN ────────────────────────────
// "Ruin 1 being and its Soul" — the whole stack, no descent. There were
// THREE hand-rolled copies of this on the player's side (Weeping Dagger,
// Solomon Godfry × 2 sides, and Shatterfield's Necklace of Congealed Death)
// and they had already drifted apart in three ways (2026-09-14):
//
//   · R123 · THE TOME WARD. applyTomeEffect shadows `ctx.updatePlayer` for
//     the whole activation so Shower of Lyt and Phoenix Aura cover every
//     write inside it. Shatterfield's handleFeedATV is OUTSIDE that shadow
//     and writes the raw component updatePlayer — so the brain's Necklace
//     fizzled against a ward the player paid for and the player's walked
//     straight through it. THIS function is the seam handleFeedATV should
//     call instead; passing it a ctx built from the component's state puts
//     that path back under the ward.
//   · C13 · THE WARD SOAK. "Pick a being, ruin it" IS an attack on that
//     being: Lyt Hearted honours `nullifyNextAttack` and so does the
//     brain's execRuin, and Weeping Dagger did not. A 6-Khao Lyt Footed
//     ward stopped the brain's dagger and not the player's.
//   · THE EMPTIED SLOT. The copies cleared six keys; the brain's
//     ruinPlayerBeingFull clears thirteen, so a ruined slot kept a stale
//     `mustBeRuinedFirst` / `nullifyNextAttack` / `intangible` on the
//     player's side and not on the brain's. The key set below is a
//     deliberate mirror of NPC.ruinPlayerBeingFull — if one moves, both move.
//
// R68 (the Heretical Tome), the wielded-Treasure cascade and the Akula
// listener all live here now, once, instead of three times.
//
// ⚠ ctx IS THIRD, IN FRONT OF THE OPTIONAL — see the banner over
// descendBeing. `soak: false` is for a card that COMPELS its own
// controller (Solomon Godfry's mutual sacrifice), which is not an attack
// and therefore not something a ward may answer; the brain agrees — its
// applySolomonGodfryEffect does not consult the flag either.
//
// Returns "ruined" | "soaked" | null (nothing there to ruin).
export const ruinBeingAndSoul = async (targetPlayerIdx, slotIdx, ctx, opts = {}) => {
  const { soak = true, label = null } = opts;
  const pData = livePlayer(targetPlayerIdx, targetPlayerIdx === ctx.playerIdx ? ctx.me : ctx.opponent, ctx);
  const field = JSON.parse(JSON.stringify(pData?.field || {}));
  const slot = field.esclings?.[slotIdx];
  if (!slot?.escendant) return null;
  const beingName = getCard(slot.escendant)?.name || "that being";
  if (soak && slot.nullifyNextAttack) {
    field.esclings[slotIdx] = { ...slot, nullifyNextAttack: false };
    await ctx.updatePlayer(ctx.code, targetPlayerIdx, { field });
    ctx.showToast(`◈ ${beingName}'s ward soaked the ruin — nullified!`, GM.inkGold);
    return "soaked";
  }
  const necropolis = [...(pData?.necropolis || []), slot.escendant, ...(slot.soul || []),
                      ...(slot.heretical ? [slot.heretical] : [])];   // 🔥 R68
  field.esclings[slotIdx] = {
    ...slot, escendant: null, currentLyf: null, soul: [], soulUsed: {}, heretical: null,
    fallen: false, fallenTurn: null, attackedThisTurn: false, intangible: false,
    mustBeRuinedFirst: false, nullifyNextAttack: false, reflectNextAttack: false,
    nullifiedSouls: [],
  };
  cascadeWieldedTreasures(field, necropolis, slotIdx);
  await ctx.updatePlayer(ctx.code, targetPlayerIdx, { field, necropolis });
  if (label) ctx.showToast(`✦ ${label} — ${beingName} and its Soul ruined!`, SD.red);
  await fireAkulaListenerOnRuin(ctx);
  return "ruined";
};

export const getRequiredTarget = (oppField) => {
    if (!oppField) return null;
    // Escling-slot flag first (Quasar-style — activated state, stays flag-based)
    const esclingMatch = (oppField.esclings || []).findIndex(s => s?.escendant && s?.mustBeRuinedFirst);
    if (esclingMatch >= 0) {
      const s = oppField.esclings[esclingMatch];
      return { kind: "escling", slotIdx: esclingMatch, id: s.escendant, name: getCard(s.escendant)?.name || "this being" };
    }
    // Tome-slot requirement (D'uhmdumn-style) — INTRINSIC, derived from id.
    // Face-up only; face-down tomes are hidden and impose no block.
    const tomeMatch = (oppField.tomes || []).findIndex(
      t => t?.id && !t?.faceDown && TOME_RUIN_FIRST_IDS.has(t.id)
    );
    if (tomeMatch >= 0) {
      const t = oppField.tomes[tomeMatch];
      return { kind: "tome", slotIdx: tomeMatch, id: t.id, name: getCard(t.id)?.name || "this Tome" };
    }
    return null;
  };

export const applyTomeRuinClauses = async (ownerIdx, tomeId, ctx) => {
    try {
      const owner = ctx.matchRef.current?.players?.[ownerIdx];
      if (!owner) return;
      if (tomeId === "tome_restraining_ordeal") {
        const na = sealAnimaGain((owner.anima ?? STARTING_ANIMA), 2);
        await ctx.updatePlayer(ctx.code, ownerIdx, { anima: na, ...buildAnimaGainUpdate(owner, 2) });
        ctx.showToast("◆ Restraining Ordeal ruined — its owner gains 2 Anima.", GM.vellumFaint);
      }
      if (tomeId === "tome_king_of_the_castle") {
        const newG = { ...(owner.grimoire || {}) };
        const pool = Object.entries(newG).flatMap(([gid, qty]) => Array(qty).fill(gid));
        // R10 — draw-then-discard: the grant resolves at the cap; the
        // owner discards after (the `< HAND_LIMIT` block was a hard cap).
        if (pool.length > 0) {
          const d = pool[Math.floor(Math.random() * pool.length)];
          if (newG[d] > 1) newG[d]--; else delete newG[d];
          await ctx.updatePlayer(ctx.code, ownerIdx, { hand: [...(owner.hand || []), d], grimoire: newG });
          ctx.showToast("◆ King of the Castle sent to Necropolis — its owner drew 1 card.", GM.vellumFaint);
        }
      }
    } catch (_) { /* clause grants must never break the ruin itself */ }
  };

export const attemptRuinOppTome = async (fieldDraft, tomeIdx, ctx) => {
    const tome = fieldDraft?.tomes?.[tomeIdx];
    if (!tome?.id) return false;
    // ── R125 · AN INTANGIBLE TREASURE CANNOT BE RUINED ──────────
    // ⚠ THIS FUNNEL ASKED ABOUT `nullifyFirstRuin` AND NOTHING ELSE, so
    // three shipped cards that print intangibility were untouchable in
    // their own text and perfectly ruinable in play. Asked FIRST,
    // because intangibility is not a shield that gets consumed — it is
    // a window, and a window should not eat a one-shot shield.
    if (tomeIsIntangible(tome, ctx.match?.turn ?? 0, ctx.match?.seatCount ?? 2)) {
      ctx.showToast(`🫧 ${getCard(tome.id)?.name || "That Treasure"} is Intangible — it cannot be ruined.`, GM.inkGold);
      return false;
    }
    if (tome.nullifyFirstRuin) {
      // Consume the shield — tome stays, flag clears
      fieldDraft.tomes[tomeIdx] = { ...tome, nullifyFirstRuin: false };
      ctx.showToast(`◈ ${getCard(tome.id)?.name || "Tome"} — First ruin nullified!`, GM.inkGold);
      return false;
    }
    fieldDraft.tomes[tomeIdx] = null;
    // On-ruin clauses fire for the tome's owner (the opponent in this funnel).
    await applyTomeRuinClauses(ctx.oppIdx, tome.id, ctx);
    return true;
  };

export const applyTomeEffect = async (card, ctx) => {
    if (!card) return;
    // ── WAVE 4 (C12 c): FUNNEL-ENTRY SHADOWS ────────────────────
    // Inside this dispatcher, `me` and `opponent` now read the match
    // AS OF THIS ACTIVATION, not as of the last React render. Every
    // caller awaits a response window BEFORE calling here, so the
    // component-scope closures are pre-window by definition — and all
    // ~100 effect branches were baking that staleness into their
    // clones and writes (the PvP generic-interrupt-on-ACTIVATION
    // clobber). Two lexical shadows upgrade the whole dispatcher to
    // post-window truth in one place. Writes inside pickers can still
    // age while a picker is held open — same as before, and the LATE
    // re-evictor covers the known abuser of that window.
    const me = withEffectiveAnima(ctx.match, ctx.matchRef.current?.players?.[ctx.playerIdx] ?? ctx.match?.players?.[ctx.playerIdx], ctx.playerIdx);
    const opponent = withEffectiveAnima(ctx.match, ctx.matchRef.current?.players?.[ctx.oppIdx] ?? ctx.match?.players?.[ctx.oppIdx], ctx.oppIdx);
    // ── C7: count this resolved activation by ilk (Tomes only —
    // Curios auto-activating through this funnel don't count) ──
    try {
      if (card.type === "Tome") {
        const ilk = card.ilk || "";
        const st = ctx.factionStatsRef.current;
        if (ilk === "Nihil")                st.nihil   += 1;
        else if (ilk === "Omnia")           st.omnia   += 1;
        else if (ilk === "Ancient Sorcery") st.ancient += 1;
        else                                st.otherTomes += 1;
      }
    } catch (_) { /* instrumentation must never break an effect */ }
    // ── WAVE 4 (C12): ALL OR NOTHING — the standing interrupt fires ──
    // The armed side is honored at the ONE funnel every player-side Tome
    // activation passes through. If the ADVERSARY's field carries the
    // flag, this activation is intercepted: the flag is consumed, the
    // activating tome is ruined instead, and the effect never resolves.
    // Fares are NOT REFUNDED (Sword & Shield semantics). ⚠ this line
    // used to say 'canon: paid once', which now reads as the opposite of
    // R105 — it never meant that; it means an answered firing keeps its
    // cost.
    // Counters fired from response windows route through
    // fireCounterFromWindow, never through here — naturally exempt.
    // Callers stop on the "AON_RUINED" sentinel so their one-shot/late
    // lifecycles don't double-ride the Necropolis. The OTHER direction
    // (the player arms it against the NPC's own activations inside
    // runNPCTurn) stays gated on the C12(a) runner migration — one
    // flag, and every funnel honors it as it becomes safe to write from.
    if (card.type === "Tome" && ctx.matchRef.current?.players?.[ctx.oppIdx]?.field?.allOrNothingActive) {
      const meLiveAON = ctx.matchRef.current?.players?.[ctx.playerIdx] || me;
      const mfT = meLiveAON?.field?.tomes || [];
      const aonTi = mfT.findIndex(t => t && typeof t === "object" && t.id === card.id);
      // Consume THEIR standing flag + ruin MY tome — direct path writes,
      // nothing else on either field is touched.
      await ctx.updatePlayer(ctx.code, ctx.oppIdx, { "field/allOrNothingActive": false });
      const aonUpd = { necropolis: [...(meLiveAON?.necropolis || []), card.id] };
      if (aonTi >= 0) aonUpd[`field/tomes/${aonTi}`] = null;
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, aonUpd);
      ctx.showToast(`✕ All or Nothing — ${card.name} is ruined instead! The standing interrupt is spent.`, SD.red);
      return "AON_RUINED";
    }

    // ══════════════════════════════════════════════════════════
    // 🛑 THE OTHER THREE STANDING INTERRUPTS (2026-09-13)
    // ══════════════════════════════════════════════════════════
    // ⚠ THESE WERE THREE DEAD CARDS. All three wrote a field flag and
    // NOTHING ANYWHERE READ IT — not this file, not Shatterfield, not
    // NPC.js, not ResponseBridge:
    //
    //   · **Sword and Shield of O'mnis** — *"If your adversary attempts
    //     to activate a card effect, or one is in use, ruin it instead."*
    //     Set `swordShieldOmnisActive`. Read by nobody.
    //   · **Rays of Hope** — *"Nullify the effect, or activation, of 1 of
    //     your adversary's cards, except a Nihil Tome, for the rest of
    //     the turn."* Set `nihilShieldActive` + `nihilShieldTurn`.
    //   · **Cleansing Burst of Nihil** — the same, plus *"if it's an
    //     Omnia Tome or Curio, ruin it."* Set `nihilRuinOmnia` and
    //     `nihilProtectCurios` on top.
    //
    // They live HERE, beside All or Nothing, because that is the one
    // funnel every player-side Tome activation passes through — the same
    // argument the ward below makes and the same one the AON block above
    // already made. Three interrupts in three places would be three
    // chances to disagree about the order they fire in.
    //
    // THE ORDER IS THE PRINTED SEVERITY. Sword and Shield RUINS, so it
    // is asked first: a Tome that is being ruined is not a Tome whose
    // effect needs nullifying. Then the Nihil shields, which void the
    // effect and keep the fare (Nihil semantics, as everywhere else).
    //
    // ⚠ EACH IS SPENT BY FIRING. "1 of your adversary's cards" is one
    // card, and "for the rest of the turn" is the WINDOW it waits in,
    // not the number it stops. The turn stamp is what expires it.
    {
      const oppLive = ctx.matchRef.current?.players?.[ctx.oppIdx] || opponent;
      const of = oppLive?.field || {};
      const now = ctx.match?.turn || 0;
      const meLiveSI = ctx.matchRef.current?.players?.[ctx.playerIdx] || me;
      const myTomesSI = meLiveSI?.field?.tomes || [];
      const myTi = myTomesSI.findIndex(t => t && typeof t === "object" && t.id === card.id);
      const ruinMine = async (why) => {
        const upd = { necropolis: [...(meLiveSI?.necropolis || []), card.id] };
        if (myTi >= 0) upd[`field/tomes/${myTi}`] = null;
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, upd);
        ctx.showToast(why, SD.red);
      };

      if (of.swordShieldOmnisActive) {
        await ctx.updatePlayer(ctx.code, ctx.oppIdx, { "field/swordShieldOmnisActive": false });
        await ruinMine(`✕ Sword and Shield of O'mnis — ${card.name} is ruined instead of resolving.`);
        return "AON_RUINED";
      }

      // ⚠ "EXCEPT A NIHIL TOME" IS PRINTED ON BOTH CARDS and is the one
      // clause a reader is most likely to drop: a Nihil Tome walks
      // through both shields untouched.
      const nihilUp = of.nihilShieldActive && (of.nihilShieldTurn ?? -1) === now;
      if (nihilUp && card.ilk !== "Nihil") {
        const ruinIt = of.nihilRuinOmnia
          && (card.ilk === "Omnia" || (of.nihilProtectCurios && card.type === "Curio"));
        await ctx.updatePlayer(ctx.code, ctx.oppIdx, {
          "field/nihilShieldActive": false,
          "field/nihilRuinOmnia": false,
          "field/nihilProtectCurios": false,
        });
        if (ruinIt) {
          await ruinMine(`✕ Cleansing Burst of Nihil — ${card.name} is nullified AND ruined.`);
          return "AON_RUINED";
        }
        ctx.showToast(`◈ ${card.name}'s effect is nullified — the fare stays paid.`, GM.inkGold);
        return;
      }
    }

    // ── R123 · THE TOME WARD, ANSWERED AT THE PAYLOAD ───────────
    // caP'n, 2026-09-13: *"Cards is general and Beings is specific, written
    // and chosen specifically for those purposes."* Shower of Lyt wards
    // BEINGS for the turn; Phoenix Aura wards CARDS for two full Turns.
    //
    // ⚠ IT IS A WRAPPER ON THE WRITE, NOT A GUARD AT THE HEAD, and that
    // distinction is why this can sit at the head. A guard that RETURNS
    // early here does not ward the adversary's cards — it turns off their
    // whole Tome lane, their healing and their Khao growth included (that
    // version was written on 2026-09-08 and reverted the same day). This
    // asks the PAYLOAD what it would do, exactly as R08's seal does, so a
    // self-directed write and an adversary-side GIFT both pass through
    // untouched and only harm of the warded kind is dropped.
    //
    // ⭐ AND IT USED TO BE INSTALLED 260 LINES FURTHER DOWN, under
    // "PROTECTION CHECKS", which put it BELOW the Treasure block — so
    // WEEPING DAGGER, the one adversary-harming Tome up there ("Ruin 1
    // being and its Soul", OP3, 10 Khao), returned before the ward existed.
    // Shower of Lyt and Phoenix Aura simply did not stop it, while the
    // BRAIN's ward shadow sits at the top of applyNpcTomeFireEffects and
    // covers the identical row. A player paid 3 or 8 Khao, watched the
    // NPC's dagger fizzle, then watched their own walk straight through.
    // Installed here, it covers every branch below it and every branch
    // written tomorrow — which was the argument for a wrapper all along.
    //
    // ⚠ THE WRITE IS DROPPED WHOLE rather than stripped key by key: a
    // half-applied effect is worse than a fizzled one, and a fizzle against
    // a warded target is a shape Terror already has — it is what Nihil
    // does. The fare stays paid, which is R105 and Sword & Shield both.
    // ONE declaration of each, here, read by the ward below and by Divine
    // Justice's ilk guard further down. `opponent` is already the
    // post-window live read taken at the head of this function, and the
    // ward's SCOPE and its `before` snapshot must come from the same object
    // or tomeWardBlocks is diffing two different boards.
    const oppField = opponent?.field || {};
    const currentTurn = ctx.match?.turn || 0;
    {
      const wardScope = tomeWardScope(oppField, currentTurn);
      if (wardScope) {
        const wardedBefore = opponent;
        const innerUpdate = ctx.updatePlayer;
        const wardedOppIdx = ctx.oppIdx;
        const notify = ctx.showToast;
        let wardToasted = false;
        ctx = { ...ctx, updatePlayer: async (code2, idx2, updates2) => {
          if (idx2 === wardedOppIdx) {
            const blocked = tomeWardBlocks(wardedBefore, updates2, wardScope);
            if (blocked) {
              if (!wardToasted) {
                wardToasted = true;
                notify(
                  wardScope === "cards"
                    ? `◈ Phoenix Aura — their cards are warded from Tomes (${blocked}).`
                    : `◈ Shower of Lyt — their beings are warded from Tomes (${blocked}).`,
                  GM.inkGold);
              }
              return;
            }
          }
          return innerUpdate(code2, idx2, updates2);
        } };
      }
    }

    const id = card.id;
    // Khao Garden (L1) — "Add +1 to your Max Khao at the end of the turn."
    // The actual +1 is applied in handleEndTurn's tome loop. Here we only
    // confirm activation and queue a note. Double-apply bug fixed Apr 2026.
    if (id === "tome_khao_garden") { ctx.showToast(`✦ Khao Garden — Max Khao will rise at end of turn!`, SD.gold); return; }
    // Khao Arcadia (L2) — same "end of turn" semantics as Khao Garden.
    if (id === "tome_khao_arcadia") { ctx.showToast(`✦ Khao Arcadia — Max Khao will rise by 2 at end of turn!`, SD.gold); return; }
    // Khao Paradise (L3) — "This card is Intangible the turn it was
    // constructed. Add +2 to your Max Khao." Intangible flag set via
    // constructedTurn marker; max khao bump is immediate per text (no
    // "at end of turn" clause).
    if (id === "tome_khao_paradise") {
      const newMax = Math.min(MAX_KHAO_ABS, (me?.maxKhao || STARTING_KHAO) + 2);
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      const tIdx = (f.tomes || []).findIndex(t => t?.id === "tome_khao_paradise");
      if (tIdx >= 0) f.tomes[tIdx] = { ...f.tomes[tIdx], constructedTurn: ctx.match?.turn || 0, intangible: true };
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { maxKhao: newMax, field: f });
      ctx.showToast(`✦ Khao Paradise — Intangible this turn. Max Khao → ${newMax}!`, SD.gold);
      return;
    }
    if (id === "tome_bhodi_tree") { const gain = 1; const newAnima = sealAnimaGain((me?.anima || STARTING_ANIMA), gain); await ctx.updatePlayer(ctx.code, ctx.playerIdx, { anima: newAnima, ...buildAnimaGainUpdate(me, gain) }); ctx.showToast(`✦ +1 Anima! (${newAnima})`, SD.red); return; }
    if (id === "tome_anima_house") { const gain = 2; const newAnima = sealAnimaGain((me?.anima || STARTING_ANIMA), gain); await ctx.updatePlayer(ctx.code, ctx.playerIdx, { anima: newAnima, ...buildAnimaGainUpdate(me, gain) }); ctx.showToast(`✦ +2 Anima! (${newAnima})`, SD.red); return; }
    // Stars and Skies (L3) — "This Tome is Intangible the turn it was
    // constructed. You gain +5 Anima."
    if (id === "tome_stars_and_skies") {
      const gain = 5;
      const newAnima = sealAnimaGain((me?.anima || STARTING_ANIMA), gain);
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      const tIdx = (f.tomes || []).findIndex(t => t?.id === "tome_stars_and_skies");
      if (tIdx >= 0) f.tomes[tIdx] = { ...f.tomes[tIdx], constructedTurn: ctx.match?.turn || 0, intangible: true };
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { anima: newAnima, field: f, ...buildAnimaGainUpdate(me, gain) });
      ctx.showToast(`✦ Stars and Skies — Intangible this turn. +5 Anima!`, SD.red);
      return;
    }
    // ── TOME EFFECTS ──────────────────────────────────────────────

    // ── Khao D'uhmdumn — feedback only ──────────────────────────
    // The ruin-first REQUIREMENT now self-derives from card id in
    // getRequiredTarget, and the Lord's consumable nullifyFirstRuin is set
    // deterministically inline at every placement/flip path. This block used
    // to scan a stale me.field to write the flags and could silently miss
    // (root of Bug #4) — that scan is removed. We only surface the toast here.
    if (id.includes("khao_duhmdumn")) {
      // ⭐ THE LORD'S SOAK IS STAMPED HERE (2026-09-15, one resolver). It was
      // stamped at four placement/flip sites in Shatterfield and once in the
      // brain's old executor — five copies of one flag. Every activation
      // passes through this funnel, so this is the one place it is written.
      if (id === "tome_khao_duhmdumn_lord") {
        const meD = livePlayer(ctx.playerIdx, ctx.me, ctx);
        const tD = meD?.field?.tomes || [];
        for (let i = 0; i < 3; i++) {
          const tt = tD[i];
          if (tt && typeof tt === "object" && tt.id === id && !tt.faceDown && !tt.nullifyFirstRuin) {
            await ctx.updatePlayer(ctx.code, ctx.playerIdx, { [`field/tomes/${i}/nullifyFirstRuin`]: true });
            break;
          }
        }
      }
      const msg = id === "tome_khao_duhmdumn_lord"
        ? `✦ ${card.name} — Must ruin first, and first ruin attempt is nullified!`
        : `✦ ${card.name} — Adversary must ruin this Tome first!`;
      ctx.showToast(msg, SD.gold); return;
    }

    // ── Treasure Tomes ─────────────────────────────────────────
    // Boots of Wing Walking — OPT: Make 1 being Intangible
    if (id === "tome_boots_wing_walking") {
      const aliveSlots = (me?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      if (aliveSlots.length === 0) { ctx.showToast("No beings to make Intangible.", GM.vellumFaint); return; }
      ctx.setTargetPicker({ title: "◆ Boots of Wing Walking — Choose a being", options: aliveSlots.map(({ s, i }) => ({
        id: String(i), label: `Being ${i+1}: ${getCard(s.escendant)?.name}`, slotIdx: i,
      })), onPick: async (opt) => {
        const f = JSON.parse(JSON.stringify(me?.field || {}));
        f.esclings[opt.slotIdx] = { ...f.esclings[opt.slotIdx], intangible: true };
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
        ctx.showToast(`✦ Boots of Wing Walking — ${getCard(me?.field?.esclings[opt.slotIdx]?.escendant)?.name} is Intangible!`, GM.inkGold);
        ctx.setTargetPicker(null);
      }});
      return;
    }
    // Gauntlets of A'zka — wielder +2 Lyt (bond via wielder picker in handlePlayTome)
    if (id === "tome_gauntlets_azka" || id === "tome_lightning_blade") {
      // The effect is passive (getEffectiveLytPure / the attack path read
      // `wieldedBy`); activation's whole job is the BOND, made here.
      if (!(await bondWielderIfNeeded(card, ctx))) ctx.showToast(`✦ ${card.name} — bonded.`, SD.gold);
      return;
    }
    // Sword of Shadows — OPT: wielder extra Lyt Attack
    if (id === "tome_sword_of_shadows") {
      // Wave 4: read wieldedBy from the Tome rift, not the legacy soul
      // attachment path. If the Sword is bonded, grant the wielder an
      // extra Lyt Attack this turn via the extraAttack flag.
      const myTomes = me?.field?.tomes || [];
      const swordSlot = myTomes.findIndex(t => t?.id === "tome_sword_of_shadows" && !t?.faceDown);
      if (swordSlot < 0) { ctx.showToast("Sword of Shadows must be on the field face-up.", GM.vellumFaint); return; }
      const wieldedBy = myTomes[swordSlot]?.wieldedBy;
      if (wieldedBy === null || wieldedBy === undefined) {
        // First activation: bond a wielder (one resolver, 2026-09-15). The
        // extra swing is granted from the next activation on, exactly as
        // before — the bond was always a separate step.
        await bondWielderIfNeeded(card, ctx);
        return;
      }
      const wielderSlot = me?.field?.esclings?.[wieldedBy];
      if (!wielderSlot?.escendant) {
        ctx.showToast("Sword of Shadows — wielder not present.", SD.red); return;
      }
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.esclings[wieldedBy] = { ...f.esclings[wieldedBy], extraAttack: true };
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast(`✦ Sword of Shadows — ${getCard(wielderSlot.escendant)?.name} can Lyt Attack again!`, SD.gold); return;
    }
    // Weeping Dagger — OP3: Ruin 1 being and its Soul
    if (id === "tome_weeping_dagger") {
      const oppAlive = (opponent?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      if (oppAlive.length === 0) { ctx.showToast("No beings to ruin.", GM.vellumFaint); return; }
      // ── R-MRF: can't ruin a being while priority target stands ──
      const mrf = getRequiredTarget(opponent?.field);
      if (mrf) {
        ctx.showToast(`${mrf.name} must be ruined first!`, SD.red); return;
      }
      ctx.setTargetPicker({ title: "◆ Weeping Dagger — Ruin 1 being and its Soul", options: oppAlive.map(({ s, i }) => ({
        id: String(i), label: `${getCard(s.escendant)?.name}`, slotIdx: i, escendantId: s.escendant,
      })), onPick: async (opt) => {
        // ⭐ ONE full being-ruin, shared — see ruinBeingAndSoul. This branch
        // used to hand-roll it and was the copy that DID NOT honour the C13
        // ward soak, so a ward the player bought stopped the brain's
        // identical ruin and not their own.
        await ruinBeingAndSoul(ctx.oppIdx, opt.slotIdx, ctx, { label: "Weeping Dagger" });
        ctx.setTargetPicker(null);
      }});
      return;
    }

    // ── Wave 4: Chief Calidum's Cuirass — OP3 Lyf transfer ─────
    // Card text: "Transfer 3 Lyf to any 1 card with Lyf for 3 turns.
    // Cuirass sits at 0 Lyf during the lend (vulnerable). If Cuirass is
    // ruined during lend, the buff revokes early. After 3 turns, Lyf
    // returns to Cuirass."
    //
    // State kept on tome slot:
    //   cuirassLentTo: { kind: "escendant"|"tome", slotIdx, playerIdx }
    //   cuirassLentUntil: match turn (inclusive) — after this turn, revert
    // State kept on target card:
    //   cuirassBuffFrom: { tomeSlotIdx, playerIdx } — back-pointer
    //
    // Turn-end cleanup runs in resetTurnState and reverts when turn > cuirassLentUntil.
    if (id === "tome_chief_calidum") {
      const myTomes = me?.field?.tomes || [];
      const cuirassSlot = myTomes.findIndex(t => t?.id === "tome_chief_calidum" && !t?.faceDown);
      if (cuirassSlot < 0) { ctx.showToast("Chief Calidum's Cuirass must be on the field face-up.", GM.vellumFaint); return; }
      const cuirassTome = myTomes[cuirassSlot];
      if (cuirassTome?.cuirassLentTo) {
        ctx.showToast("Chief Calidum's Cuirass is already lending Lyf.", GM.vellumFaint); return;
      }
      if ((cuirassTome?.currentLyf ?? 3) < 3) {
        ctx.showToast("Chief Calidum's Cuirass needs 3 Lyf to lend.", SD.red); return;
      }
      // Build pick list — any card (either side) with current Lyf > 0.
      // Escendants: my side + opp side.
      // Tomes with Lyf: my side + opp side (Khao D'uhmdumn, Treasures with Lyf).
      const options = [];
      (me?.field?.esclings || []).forEach((s, i) => {
        if (!s?.escendant) return;
        const c = getCard(s.escendant);
        const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (c?.lyf ?? 0);
        if (cur <= 0) return;
        options.push({
          id: `my-esc-${i}`, label: `(You) ${c?.name}`,
          target: { kind: "escendant", slotIdx: i, side: "me" },
        });
      });
      (opponent?.field?.esclings || []).forEach((s, i) => {
        if (!s?.escendant) return;
        const c = getCard(s.escendant);
        const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (c?.lyf ?? 0);
        if (cur <= 0) return;
        options.push({
          id: `opp-esc-${i}`, label: `(Opp) ${c?.name}`,
          target: { kind: "escendant", slotIdx: i, side: "opp" },
        });
      });
      (me?.field?.tomes || []).forEach((t, i) => {
        if (!t?.id || i === cuirassSlot) return;
        const c = getCard(t.id);
        const baseLyf = c?.lyf ?? 0;
        if (baseLyf <= 0) return;
        const cur = t.currentLyf !== null && t.currentLyf !== undefined ? t.currentLyf : baseLyf;
        if (cur <= 0) return;
        options.push({
          id: `my-tome-${i}`, label: `(You) ${c?.name}`,
          target: { kind: "tome", slotIdx: i, side: "me" },
        });
      });
      (opponent?.field?.tomes || []).forEach((t, i) => {
        if (!t?.id) return;
        const c = getCard(t.id);
        const baseLyf = c?.lyf ?? 0;
        if (baseLyf <= 0) return;
        const cur = t.currentLyf !== null && t.currentLyf !== undefined ? t.currentLyf : baseLyf;
        if (cur <= 0) return;
        options.push({
          id: `opp-tome-${i}`, label: `(Opp) ${c?.name}`,
          target: { kind: "tome", slotIdx: i, side: "opp" },
        });
      });
      if (options.length === 0) {
        ctx.showToast("No valid target — need a card with Lyf.", GM.vellumFaint); return;
      }
      // ── R103 · "for 3 turns" IS THREE FULL TURNS ────────────────
      // Chief Calidum's Cuirass prints "for 3 turns" and this line read
      // `turn + 3` — raw half-turns, so the loan expired in half the
      // printed time. It is the third and last duration in the engine
      // that was written as a bare offset; all three now route through
      // rules.durationExpiryTurn, which is the same converter the
      // OPT/OP2/OP3 cooldowns use. caP'n, 2026-08-26: *"if it's just
      // broad '2 turns' that means 2 full turns which consists of 4 ×
      // 1/2 turns."*
      const currentTurnCuirass = ctx.match?.turn || 0;
      const lendUntil = durationExpiryTurn(currentTurnCuirass, 3, ctx.match?.seatCount);
      ctx.setTargetPicker({
        title: "◆ Chief Calidum's Cuirass — Lend 3 Lyf (3 turns)",
        options,
        onPick: async (opt) => {
          const t = opt.target;
          // Write both sides atomically when possible.
          // Cuirass: currentLyf -> 0, cuirassLentTo, cuirassLentUntil
          const myField = JSON.parse(JSON.stringify(me?.field || {}));
          myField.tomes[cuirassSlot] = {
            ...myField.tomes[cuirassSlot],
            currentLyf: 0,
            cuirassLentTo: { kind: t.kind, slotIdx: t.slotIdx, side: t.side, seat: t.side === "opp" ? ctx.oppIdx : ctx.playerIdx },   // `seat` so the seam can return the loan at any table size
            cuirassLentUntil: lendUntil,
          };
          if (t.side === "me") {
            if (t.kind === "escendant") {
              const s = myField.esclings[t.slotIdx];
              const c = getCard(s.escendant);
              const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (c?.lyf ?? 0);
              myField.esclings[t.slotIdx] = {
                ...s, currentLyf: cur + 3,
                cuirassBuffFrom: { tomeSlotIdx: cuirassSlot, side: "me" },
              };
            } else {
              const tomeTarget = myField.tomes[t.slotIdx];
              const c = getCard(tomeTarget.id);
              const baseLyf = c?.lyf ?? 0;
              const cur = tomeTarget.currentLyf !== null && tomeTarget.currentLyf !== undefined ? tomeTarget.currentLyf : baseLyf;
              myField.tomes[t.slotIdx] = {
                ...tomeTarget, currentLyf: cur + 3,
                cuirassBuffFrom: { tomeSlotIdx: cuirassSlot, side: "me" },
              };
            }
            await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: myField });
          } else {
            // Opp side — write my field (Cuirass update) + opp field (target update) separately
            await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: myField });
            const oppField = JSON.parse(JSON.stringify(opponent?.field || {}));
            if (t.kind === "escendant") {
              const s = oppField.esclings[t.slotIdx];
              const c = getCard(s.escendant);
              const cur = s.currentLyf !== null && s.currentLyf !== undefined ? s.currentLyf : (c?.lyf ?? 0);
              oppField.esclings[t.slotIdx] = {
                ...s, currentLyf: cur + 3,
                cuirassBuffFrom: { tomeSlotIdx: cuirassSlot, side: "me" },
              };
            } else {
              const tomeTarget = oppField.tomes[t.slotIdx];
              const c = getCard(tomeTarget.id);
              const baseLyf = c?.lyf ?? 0;
              const cur = tomeTarget.currentLyf !== null && tomeTarget.currentLyf !== undefined ? tomeTarget.currentLyf : baseLyf;
              oppField.tomes[t.slotIdx] = {
                ...tomeTarget, currentLyf: cur + 3,
                cuirassBuffFrom: { tomeSlotIdx: cuirassSlot, side: "me" },
              };
            }
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: oppField });
          }
          ctx.showToast(`✦ Chief Calidum's Cuirass — 3 Lyf lent for 3 turns!`, SD.gold);
          ctx.setTargetPicker(null);
        },
      });
      return;
    }

    // ── PROTECTION CHECKS ────────────────────────────────────────
    // `oppField` and `currentTurn` are declared ONCE, at the head of this
    // dispatch, beside the R123 ward that also reads them.
    //
    // ⚠ DIVINE JUSTICE STAYS HERE, not at the head, and that is deliberate:
    // it is an ILK-SCOPED EARLY RETURN ("nullify all Omnia Tomes for 2
    // turns"), so it refuses exactly what its card names and nothing else.
    // The R123 ward moved up because it is a WRAPPER that asks the payload;
    // this is a REFUSAL, and a refusal at the head is the shape that was
    // written on 2026-09-08 and reverted the same day.
    // ⭐ READ THE HELPER, NEVER THE COMPARISON (caP'n, 2026-09-14).
    // This line was `currentTurn <= oppField.divineJusticeUntilTurn` — the
    // boundary spelled out by hand, in the one file that also STAMPS the
    // expiry through `durationExpiryTurn` two hundred lines down. caP'n
    // settled the boundary for the whole engine:
    //
    //   *"3 turns or any type of broad reference always refers to a full
    //   turn; I will specify with words like 'on your turn' or 'your
    //   opponent's turn' something like that, but 'for 3 turns' is way
    //   too broad and that means 6 half turns, ONCE THE 6 HALF TURNS ARE
    //   OVER, YOU CAN PROCEED."*
    //
    // So the window is EXCLUSIVE, and `rules.durationStillRunning` is
    // being changed from `<=` to `<` to match. A hand-written comparison
    // does not move when that lands — it just quietly disagrees with the
    // stamp it is reading, by exactly one half-turn, in the direction
    // that keeps a ward alive a beat too long. Asked through the helper,
    // it flips with the ruling and cannot drift from Phoenix Aura, the
    // Cuirass, or the cooldowns that share the same arithmetic.
    const isDivineJustice = durationStillRunning(currentTurn, oppField.divineJusticeUntilTurn);
    // If opponent has Divine Justice and this is an Omnia tome — blocked
    if (isDivineJustice && card.ilk === "Omnia") {
      ctx.showToast("◈ Divine Justice blocks all Omnia Tomes!", GM.inkGold); return;
    }
    // ⚠ A 30-LINE R123 BANNER STOOD HERE AND IT ARGUED FOR ITS OWN
    // POSITION — "AND IT IS WIRED HERE, NOT AT THE HEAD OF THIS
    // DISPATCHER" — while being wired below the Treasure block, where
    // Weeping Dagger had already returned. The reasoning was right (ask
    // the PAYLOAD, never refuse at a lane's head) and the placement was
    // wrong, which is the most expensive shape a comment can take: a
    // citation that reads as a decision. The banner now lives with the
    // wrapper, at the head of the dispatch, above `const id = card.id`.

    // ── Omnia Tomes ────────────────────────────────────────────
    // Ruin 1 Tome or Curios (nothing left, regnant)
    if (id === "tome_nothing_left" || id === "tome_regnant") {
      // ⭐ DEFECT FOUND 2026-08-26, WHILE APPLYING R96 TO EVERY SELF-DIM.
      // Nothing Left's printed text is *"Dim your own Anima by 1; ruin
      // 1 Tome or Curios"* and src/cards.js carries `animaCost: 1` on
      // it. NOTHING IN THE GAME EVER CHARGED IT. Tome placement pays
      // `spendKhao(card.khaoCost)` and nothing else — animaCost is read
      // by the U'hlti fare and by the two card-face renderers, and by
      // no other code path. So the card has shipped its whole life
      // strictly better than printed. Regnant has no such clause and
      // pays nothing; the branch is shared, the cost is not.
      const nlCost = id === "tome_nothing_left" ? (getCard(id)?.animaCost || 0) : 0;
      if (tillRefusesSelfDim(nlCost, "Nothing Left", ctx)) return;   // R96
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id);
      const oppCurios = (opponent?.field?.curios || []).map((c, i) => ({ c, i })).filter(({ c }) => c);
      const options = [
        ...oppTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}`, tomeIdx: i })),
        ...oppCurios.map(({ c, i }) => ({ id: c, label: `Curio: ${getCard(c)?.name}`, curioIdx: i })),
      ];
      if (options.length === 0) { ctx.showToast("No opponent Tomes or Curios to ruin.", GM.vellumFaint); return; }
      // ⚠ PAY AFTER THE TARGET CHECK, NEVER BEFORE. This branch bails
      // out with "no targets" a few lines up; charging the self-dim
      // ahead of that bail would burn the payer's Anima for nothing.
      // (R15's pay-before-you-act is about the ACTIVATION fare, which
      // is Khao and was already taken at placement.)
      if (nlCost > 0) {
        const nlLive = ctx.matchRef.current?.players?.[ctx.playerIdx] || me;
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
          anima: Math.max(0, (nlLive?.anima ?? STARTING_ANIMA) - nlCost),
          ...buildSelfDimUpdate(nlLive, nlCost),   // R100 — SELF
        });
      }
      // ── R-MRF: if opp has a priority tome, force-target it ──
      const mrfR = getRequiredTarget(opponent?.field);
      if (mrfR?.kind === "tome") {
        const forced = options.find(o => o.tomeIdx === mrfR.slotIdx);
        if (forced) {
          ctx.showToast(`◆ ${mrfR.name} must be ruined first — auto-targeting.`, GM.inkGold);
          const f = JSON.parse(JSON.stringify(opponent?.field || {}));
          const wasRuined = await attemptRuinOppTome(f, forced.tomeIdx, ctx);
          if (wasRuined) {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), mrfR.id] });
            ctx.showToast(`✦ ${mrfR.name} ruined!`, SD.red);
          } else {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f });
          }
          return;
        }
      }
      ctx.setTargetPicker({ title: "◆ Ruin 1 Tome or Curio", options, onPick: async (opt) => {
        const f = JSON.parse(JSON.stringify(opponent?.field || {}));
        if (opt.tomeIdx !== undefined) {
          const wasRuined = await attemptRuinOppTome(f, opt.tomeIdx, ctx);
          if (wasRuined) {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), opt.id] });
            ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
          } else {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f });
          }
        } else {
          if (opt.curioIdx !== undefined) f.curios[opt.curioIdx] = null;
          // ── NECROPOLIS LAW: ruined curios hit the record too.
          await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), opt.id] });
          ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
        }
        ctx.setTargetPicker(null);
      }});
      return;
    }
    // Ruin 1 Impera (radiant armor) — or 1 Impera OR U'hlti (omnis
    // corruption jet). ONE branch, TWO cards, and until now one filter
    // between them.
    //
    // ⚠ RADIANT ARMOR DOES NOT REACH A U'HLTI. Printed text: "Dim your
    // own Anima by 1; ruin 1 IMPERA." Omnis' Corruption Jet is the card
    // that reads "Ruin 1 Impera or U'hlti" — it is the one that is
    // SUPPOSED to do both, and it pays for the privilege (level 2, no
    // self-dim, versus Radiant Armor's level 1 and a point of your own
    // Anima). Sharing the branch quietly handed the cheaper card the
    // dearer card's reach. The NPC's tome book already splits them
    // (`kinds: ["impera"]` vs `["impera", "uhlti"]`) with a note saying
    // the player side did not; this closes it.
    if (id === "tome_radiant_armor" || id === "tome_omnis_corruption") {
      // ⭐ SAME DEFECT AS NOTHING LEFT, same discovery. Radiant Armor
      // reads *"Dim your own Anima by 1; ruin 1 Impera"* and carries
      // `animaCost: 1`; the cost was never charged. Omnis' Corruption
      // Jet shares the branch and has no such clause — the split above
      // already established that these two cards are NOT the same card.
      const raCost = id === "tome_radiant_armor" ? (getCard(id)?.animaCost || 0) : 0;
      if (tillRefusesSelfDim(raCost, "Radiant Armor", ctx)) return;   // R96
      const reachesUhlti = id === "tome_omnis_corruption";
      const soulLabel = reachesUhlti ? "Impera or U'hltima" : "Impera";
      const oppPrim = (opponent?.field?.esclings || []).flatMap((s, si) =>
        (s?.soul || []).filter(id => { const c = getCard(id); return c?.type === "Impera" || (reachesUhlti && c?.type === "U'hlti"); }).map(sid => ({ id: sid, label: `${getCard(sid)?.name} (on ${getCard(s.escendant)?.name})`, slotIdx: si, soulId: sid }))
      );
      if (oppPrim.length === 0) { ctx.showToast(`No opponent ${soulLabel}s to ruin.`, GM.vellumFaint); return; }
      // ── R-MRF: can't ruin soul cards while priority target stands ──
      const mrfS = getRequiredTarget(opponent?.field);
      if (mrfS) {
        ctx.showToast(`${mrfS.name} must be ruined first!`, SD.red); return;
      }
      // ⚠ PAY AFTER THE TARGET CHECK, NEVER BEFORE. This branch bails
      // out with "no targets" a few lines up; charging the self-dim
      // ahead of that bail would burn the payer's Anima for nothing.
      // (R15's pay-before-you-act is about the ACTIVATION fare, which
      // is Khao and was already taken at placement.)
      if (raCost > 0) {
        const raLive = ctx.matchRef.current?.players?.[ctx.playerIdx] || me;
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
          anima: Math.max(0, (raLive?.anima ?? STARTING_ANIMA) - raCost),
          ...buildSelfDimUpdate(raLive, raCost),   // R100 — SELF
        });
      }
      ctx.setTargetPicker({ title: `◆ Ruin 1 ${soulLabel}`, options: oppPrim, onPick: async (opt) => {
        const f = JSON.parse(JSON.stringify(opponent?.field || {}));
        // R70 on the way out — ruining a U'hlti ruins the Affinity it carried.
        const stripR = stripSoulCard(f.esclings[opt.slotIdx].soul, opt.soulId);
        f.esclings[opt.slotIdx] = { ...f.esclings[opt.slotIdx], soul: stripR.soul };
        // ── NECROPOLIS LAW: every ruined soul card hits the record.
        await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), ...stripR.gone] });
        ctx.showToast(`✦ ${getCard(opt.soulId)?.name} ruined!`, SD.red);
        ctx.setTargetPicker(null);
      }});
      return;
    }
    // Ruin 1 card (lyt hearted)
    if (id === "tome_lyt_hearted") {
      const cost = 1;
      if (tillRefusesSelfDim(cost, "Lyt Hearted", ctx)) return;   // R96
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - cost);
      // ── AUDIT FIX (July 19 2026): track the loss. ⚠ AND CORRECTED
      // 2026-08-26 — that fix pointed it at totalAnimaLost, which is
      // the Prophecy ledger, so the card was quietly feeding Bloody
      // Hell with the payer's own blood. R100 says it must not. The
      // loss is still tracked; it is tracked in the SELF ledger.
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { anima: newAnima, ...buildSelfDimUpdate(me, cost) });
      // Target picker for any opponent card
      const oppAlive = (opponent?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id);
      const options = [
        ...oppAlive.map(({ s, i }) => ({ id: s.escendant, label: `Being: ${getCard(s.escendant)?.name}`, slotIdx: i, type: "being", escendantId: s.escendant })),
        ...oppTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}`, tomeIdx: i, type: "tome" })),
      ];
      if (options.length === 0) { ctx.showToast("No cards to ruin.", GM.vellumFaint); return; }
      // ── R-MRF: if priority target stands, force-target it ──
      const mrfL = getRequiredTarget(opponent?.field);
      if (mrfL) {
        ctx.showToast(`◆ ${mrfL.name} must be ruined first — auto-targeting.`, GM.inkGold);
        const f = JSON.parse(JSON.stringify(opponent?.field || {}));
        if (mrfL.kind === "tome") {
          // Soak-aware: the D'uhmdumn Lord's nullifyFirstRuin applies
          // here too — this path used to null the slot raw around it.
          const wasRuinedMRF = await attemptRuinOppTome(f, mrfL.slotIdx, ctx);
          if (!wasRuinedMRF) { await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f }); return; }
          await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), mrfL.id] });
        } else {
          // ── C13: a warded taunt soaks the forced ruin
          const tgtM = ctx.matchRef.current?.players?.[ctx.oppIdx]?.field?.esclings?.[mrfL.slotIdx];
          if (tgtM?.nullifyNextAttack) {
            const f3 = JSON.parse(JSON.stringify(ctx.matchRef.current?.players?.[ctx.oppIdx]?.field || {}));
            f3.esclings[mrfL.slotIdx] = { ...f3.esclings[mrfL.slotIdx], nullifyNextAttack: false };
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f3 });
            ctx.showToast(`◈ ${getCard(tgtM.escendant)?.name}'s ward soaked the ruin — nullified!`, GM.inkGold);
            return;
          }
          await descendBeing(ctx.oppIdx, mrfL.slotIdx, ctx);
        }
        ctx.showToast(`✦ ${mrfL.name} ruined!`, SD.red);
        return;
      }
      ctx.setTargetPicker({ title: "◆ Lyt Hearted — Ruin 1 card (cost 1 Anima paid)", options, onPick: async (opt) => {
        if (opt.type === "being") {
          // ── C13 (canon): a being ward soaks targeted ruins too —
          // "pick a being, ruin it" IS an attack on that being. The
          // attacker-ruin clause stays dormant: Lyt Hearted is a
          // one-shot and rides to the Necropolis regardless.
          const tgtLH = ctx.matchRef.current?.players?.[ctx.oppIdx]?.field?.esclings?.[opt.slotIdx];
          if (tgtLH?.nullifyNextAttack) {
            const f2 = JSON.parse(JSON.stringify(ctx.matchRef.current?.players?.[ctx.oppIdx]?.field || {}));
            f2.esclings[opt.slotIdx] = { ...f2.esclings[opt.slotIdx], nullifyNextAttack: false };
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f2 });
            ctx.showToast(`◈ ${getCard(tgtLH.escendant)?.name}'s ward soaked the ruin — nullified!`, GM.inkGold);
          } else {
            await descendBeing(ctx.oppIdx, opt.slotIdx, ctx);
            ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
          }
        }
        if (opt.type === "tome") {
          // ── NECROPOLIS LAW + soak-aware: route through the shared
          // helper (nullifyFirstRuin honored, ruin hits the record).
          // Previously nulled the slot raw — bypassed the Lord's soak
          // AND vanished the card.
          const fLH = JSON.parse(JSON.stringify(ctx.matchRef.current?.players?.[ctx.oppIdx]?.field || opponent?.field || {}));
          const wasRuinedLH = await attemptRuinOppTome(fLH, opt.tomeIdx, ctx);
          if (wasRuinedLH) {
            const oppNec = (ctx.matchRef.current?.players?.[ctx.oppIdx]?.necropolis) || opponent?.necropolis || [];
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: fLH, necropolis: [...oppNec, opt.id] });
            ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
          } else {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: fLH });
          }
        }
        ctx.setTargetPicker(null);
      }});
      return;
    }
    // Wall of Life — "Ruin 1 Non-Being." (Tome, Curio, Impera, U'hlti, Affinity, Prophecy — anything that isn't an Escendant)
    if (id === "tome_wall_of_life") {
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id);
      const oppCurios = (opponent?.field?.curios || []).map((c, i) => ({ c, i })).filter(({ c }) => c);
      const oppSoulCards = (opponent?.field?.esclings || []).flatMap((s, si) =>
        (s?.soul || []).filter(sid => {
          const c = getCard(sid);
          return c?.type === "Impera" || c?.type === "U'hlti" || c?.type === "Affinity";
        }).map(sid => ({ sid, slotIdx: si }))
      );
      const oppProphecy = opponent?.field?.prophecy;
      const options = [
        ...oppTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}`, tomeIdx: i })),
        ...oppCurios.map(({ c, i }) => ({ id: c, label: `Curio: ${getCard(c)?.name}`, curioIdx: i })),
        ...oppSoulCards.map(({ sid, slotIdx }) => ({ id: sid, label: `${getCard(sid)?.type}: ${getCard(sid)?.name}`, slotIdx, soulId: sid })),
        ...(oppProphecy ? [{ id: oppProphecy, label: `Prophecy: ${getCard(oppProphecy)?.name}`, prophecy: true }] : []),
      ];
      if (options.length === 0) { ctx.showToast("No Non-Beings to ruin.", GM.vellumFaint); return; }
      const mrfWoL = getRequiredTarget(opponent?.field);
      if (mrfWoL?.kind === "tome") {
        const forced = options.find(o => o.tomeIdx === mrfWoL.slotIdx);
        if (forced) {
          ctx.showToast(`◆ ${mrfWoL.name} must be ruined first — auto-targeting.`, GM.inkGold);
          const f = JSON.parse(JSON.stringify(opponent?.field || {}));
          const wasRuined = await attemptRuinOppTome(f, forced.tomeIdx, ctx);
          if (wasRuined) {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), mrfWoL.id] });
            ctx.showToast(`✦ ${mrfWoL.name} ruined!`, SD.red);
          } else {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f });
          }
          return;
        }
      }
      ctx.setTargetPicker({ title: "◆ Wall of Life — Ruin 1 Non-Being", options, onPick: async (opt) => {
        const f = JSON.parse(JSON.stringify(opponent?.field || {}));
        if (opt.tomeIdx !== undefined) {
          const wasRuined = await attemptRuinOppTome(f, opt.tomeIdx, ctx);
          if (wasRuined) {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), opt.id] });
            ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
          } else {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f });
          }
        } else {
          // R70 on the way out — a ruined U'hlti drags its Affinity along,
          // so this branch's Necropolis payload is a LIST, not one id.
          let goneWoL = [opt.id];
          if (opt.curioIdx !== undefined) f.curios[opt.curioIdx] = null;
          else if (opt.soulId !== undefined) {
            const stripW = stripSoulCard(f.esclings[opt.slotIdx].soul, opt.soulId);
            f.esclings[opt.slotIdx] = { ...f.esclings[opt.slotIdx], soul: stripW.soul };
            goneWoL = stripW.gone;
          }
          else if (opt.prophecy) f.prophecy = null;
          // ── NECROPOLIS LAW: curios, souls, and prophecies included.
          // ⭐ And a ruined Prophecy clears the PLAYER ROOT too — see
          // the note two branches up. The field copy is the art; the
          // root is the win condition.
          await ctx.updatePlayer(ctx.code, ctx.oppIdx, {
            field: f,
            ...(opt.prophecy ? { prophecy: null } : {}),
            necropolis: [...(opponent?.necropolis || []), ...goneWoL],
          });
          ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
        }
        ctx.setTargetPicker(null);
      }});
      return;
    }
    // Superque — "Dim your own Anima by 1; ruin 1 Impera and 1 U'hlti."
    if (id === "tome_superque") {
      const cost = 1;
      if (tillRefusesSelfDim(cost, "Superque", ctx)) return;   // R96
      const afterCost = Math.max(0, (me?.anima || STARTING_ANIMA) - cost);
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { anima: afterCost, ...buildSelfDimUpdate(me, cost) }); // R100
      // Collect Impera and U'hltis separately
      const oppImpera = (opponent?.field?.esclings || []).flatMap((s, si) =>
        (s?.soul || []).filter(sid => getCard(sid)?.type === "Impera").map(sid => ({ id: sid, label: `Impera: ${getCard(sid)?.name} (on ${getCard(s.escendant)?.name})`, slotIdx: si, soulId: sid }))
      );
      const oppUhltis = (opponent?.field?.esclings || []).flatMap((s, si) =>
        (s?.soul || []).filter(sid => getCard(sid)?.type === "U'hlti").map(sid => ({ id: sid, label: `U'hlti: ${getCard(sid)?.name} (on ${getCard(s.escendant)?.name})`, slotIdx: si, soulId: sid }))
      );
      if (oppImpera.length === 0 && oppUhltis.length === 0) {
        ctx.showToast("No opponent Impera or U'hltimas to ruin.", GM.vellumFaint); return;
      }
      const mrfSq = getRequiredTarget(opponent?.field);
      if (mrfSq) { ctx.showToast(`${mrfSq.name} must be ruined first!`, SD.red); return; }
      // Sequential pickers — Impera first, then U'hlti
      const ruinSoulCard = async (opt) => {
        const f = JSON.parse(JSON.stringify(opponent?.field || {}));
        // R70 on the way out — a ruined U'hlti takes its Affinity with it.
        const stripSq = stripSoulCard(f.esclings[opt.slotIdx].soul, opt.soulId);
        f.esclings[opt.slotIdx] = { ...f.esclings[opt.slotIdx], soul: stripSq.soul };
        // ── NECROPOLIS LAW: every ruined soul card hits the record.
        await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), ...stripSq.gone] });
        ctx.showToast(`✦ ${getCard(opt.soulId)?.name} ruined!`, SD.red);
      };
      if (oppImpera.length > 0 && oppUhltis.length > 0) {
        ctx.setTargetPicker({ title: "◆ Superque — Ruin 1 Impera", options: oppImpera, onPick: async (p) => {
          await ruinSoulCard(p);
          ctx.setTargetPicker({ title: "◆ Superque — Ruin 1 U'hlti", options: oppUhltis, onPick: async (u) => {
            await ruinSoulCard(u);
            ctx.setTargetPicker(null);
          }});
        }});
      } else if (oppImpera.length > 0) {
        ctx.setTargetPicker({ title: "◆ Superque — Ruin 1 Impera (no U'hltimas)", options: oppImpera, onPick: async (p) => {
          await ruinSoulCard(p); ctx.setTargetPicker(null);
        }});
      } else {
        ctx.setTargetPicker({ title: "◆ Superque — Ruin 1 U'hltima (no Impera)", options: oppUhltis, onPick: async (u) => {
          await ruinSoulCard(u); ctx.setTargetPicker(null);
        }});
      }
      return;
    }
    // All or Nothing (Omnia L3) — "If your adversary attempts to activate a
    // Tome, or one is in use, ruin it instead." Two parts:
    //   (1) On play: if opp has a face-up Tome in use now, ruin one (picker).
    //   (2) Persistent standing interrupt: future Tome activations by opp are
    //       intercepted and the tome is ruined instead. WIRED (Wave 4,
    //       2026-08-18) at applyTomeEffect's head for every PLAYER-side
    //       activation — so a PvP adversary's (or NPC's) armed flag fires
    //       against you today. The remaining direction — the NPC engine
    //       honoring YOUR armed flag inside its own turn — rides with the
    //       C12(a) runner migration.
    if (id === "tome_all_or_nothing") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.allOrNothingActive = true;
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id && !t.faceDown);
      if (oppTomes.length > 0) {
        const mrfAN = getRequiredTarget(opponent?.field);
        if (mrfAN?.kind === "tome") {
          const forced = oppTomes.find(({ i }) => i === mrfAN.slotIdx);
          if (forced) {
            const of = JSON.parse(JSON.stringify(opponent?.field || {}));
            const wasRuined = await attemptRuinOppTome(of, forced.i, ctx);
            if (wasRuined) {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), forced.t.id] });
              ctx.showToast(`✦ All or Nothing — ${getCard(forced.t.id)?.name} ruined! Standing interrupt active.`, SD.red);
            } else {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of });
              ctx.showToast(`◈ Ruin nullified. Standing interrupt active.`, GM.inkGold);
            }
            return;
          }
        }
        ctx.setTargetPicker({
          title: "◆ All or Nothing — Ruin 1 active Tome",
          options: oppTomes.map(({ t, i }) => ({ id: t.id, label: getCard(t.id)?.name || t.id, tomeIdx: i })),
          onPick: async (opt) => {
            const of = JSON.parse(JSON.stringify(opponent?.field || {}));
            const wasRuined = await attemptRuinOppTome(of, opt.tomeIdx, ctx);
            if (wasRuined) {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), opt.id] });
              ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined! Standing interrupt active.`, SD.red);
            } else {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of });
              ctx.showToast(`◈ Ruin nullified. Standing interrupt active.`, GM.inkGold);
            }
            ctx.setTargetPicker(null);
          },
        });
      } else {
        ctx.showToast("◆ All or Nothing — No active Tomes to ruin. Standing interrupt armed.", GM.inkGold);
      }
      return;
    }
    // Sword & Shield of O'mnis (Omnia L3) — "If your adversary attempts to
    // activate a card effect, or one is in use, ruin it instead." Broader
    // than All or Nothing (card effect, not just Tome). On play: ruin 1 of
    // any card in use (Tome OR Impera OR U'hlti). Stand-by interrupt flag
    // also set for broader coverage.
    if (id === "tome_sword_shield_omnis") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.swordShieldOmnisActive = true;
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id && !t.faceDown);
      const oppSoulCards = (opponent?.field?.esclings || []).flatMap((s, si) =>
        (s?.soul || []).filter(sid => {
          const c = getCard(sid);
          return c?.type === "Impera" || c?.type === "U'hlti";
        }).map(sid => ({ sid, slotIdx: si }))
      );
      const options = [
        ...oppTomes.map(({ t, i }) => ({ id: t.id, label: `Tome: ${getCard(t.id)?.name}`, tomeIdx: i })),
        ...oppSoulCards.map(({ sid, slotIdx }) => ({ id: sid, label: `${getCard(sid)?.type}: ${getCard(sid)?.name}`, slotIdx, soulId: sid })),
      ];
      if (options.length === 0) {
        ctx.showToast("◆ Sword & Shield of O'mnis — No active card effects to ruin. Standing interrupt armed.", GM.inkGold); return;
      }
      const mrfSS = getRequiredTarget(opponent?.field);
      if (mrfSS?.kind === "tome") {
        const forced = options.find(o => o.tomeIdx === mrfSS.slotIdx);
        if (forced) {
          const of = JSON.parse(JSON.stringify(opponent?.field || {}));
          const wasRuined = await attemptRuinOppTome(of, forced.tomeIdx, ctx);
          if (wasRuined) {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), mrfSS.id] });
            ctx.showToast(`✦ Sword & Shield of O'mnis — ${mrfSS.name} ruined! Standing interrupt active.`, SD.red);
          } else {
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of });
            ctx.showToast(`◈ Ruin nullified. Standing interrupt active.`, GM.inkGold);
          }
          return;
        }
      }
      ctx.setTargetPicker({
        title: "◆ Sword & Shield of O'mnis — Ruin 1 card in use",
        options,
        onPick: async (opt) => {
          const of = JSON.parse(JSON.stringify(opponent?.field || {}));
          if (opt.tomeIdx !== undefined) {
            const wasRuined = await attemptRuinOppTome(of, opt.tomeIdx, ctx);
            if (wasRuined) {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), opt.id] });
              ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined! Standing interrupt active.`, SD.red);
            } else {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of });
              ctx.showToast(`◈ Ruin nullified. Standing interrupt active.`, GM.inkGold);
            }
          } else if (opt.soulId !== undefined) {
            const stripSI = stripSoulCard(of.esclings[opt.slotIdx].soul, opt.soulId); // R70 on the way out
            of.esclings[opt.slotIdx] = { ...of.esclings[opt.slotIdx], soul: stripSI.soul };
            // NECROPOLIS LAW: every ruined soul card hits the record.
            await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: of, necropolis: [...(opponent?.necropolis || []), ...stripSI.gone] });
            ctx.showToast(`✦ ${getCard(opt.soulId)?.name} ruined! Standing interrupt active.`, SD.red);
          }
          ctx.setTargetPicker(null);
        },
      });
      return;
    }

    // ── Ancient Sorcery Tomes ──────────────────────────────────
    if (id === "tome_not_here") {
      await searchAndAdd("grimoire", c => c?.type === "Tome" && c?.level === 1, 1, "Level 1 Tome", ctx);
      return;
    }
    if (id === "tome_not_now") {
      await searchAndAdd("grimoire", c => c?.type === "Tome" && c?.level <= 2, 1, "Tome (up to L2)", ctx);
      return;
    }
    if (id === "tome_not_ever") {
      await searchAndAdd("grimoire", c => c?.type === "Tome" && c?.level <= 3, 1, "Tome (up to L3)", ctx);
      return;
    }
    if (id === "tome_blood_in_blood_out" || id === "tome_blood_money" || id === "tome_river_of_blood") {
      const maxLvl = id === "tome_blood_in_blood_out" ? 1 : id === "tome_blood_money" ? 2 : 3;
      const label = id === "tome_blood_in_blood_out" ? "Level 1 Being" : id === "tome_blood_money" ? "Being (up to L2)" : "Being (up to L3)";
      // Step 1: send one card from hand or Grimoire to the Necropolis.
      // ⚠ AND BLOOD MONEY'S IS "1 CARD", NOT "1 BEING". Its two siblings
      // print "1 being"; Blood Money prints "Send 1 card from your hand or
      // Grimoire" — that is what makes it the mid card of the family, and
      // the shared branch had narrowed all three to beings. The NPC's own
      // book has always had it right: bloodRow("tome_blood_money", {}, ...)
      // passes an EMPTY sacrifice filter against {types:["Escendant"]} on
      // the other two.
      const anyCard = id === "tome_blood_money";
      const sacrificeOk = (c) => anyCard ? !!c : c?.type === "Escendant";
      const handBeings = (me?.hand || []).filter(hid => sacrificeOk(getCard(hid)));
      const grimBeings = Object.keys(me?.grimoire || {}).filter(gid => sacrificeOk(getCard(gid)));
      const sacrificePool = [
        ...handBeings.map(hid => ({ id: `hand_${hid}`, label: `✋ ${getCard(hid)?.name} (Hand)`, source: "hand", cardId: hid })),
        ...grimBeings.map(gid => ({ id: `grim_${gid}`, label: `📖 ${getCard(gid)?.name} (Grimoire)`, source: "grimoire", cardId: gid })),
      ];
      if (sacrificePool.length === 0) { ctx.showToast("No beings to sacrifice.", GM.vellumFaint); return; }
      ctx.setTargetPicker({
        title: anyCard ? "◆ Send 1 Card to Necropolis" : "◆ Send 1 Being to Necropolis",
        options: sacrificePool,
        onPick: async (opt) => {
          const chosen = opt?.cardId || opt;
          const source = opt?.source || "hand";
          let updates = {};
          if (source === "hand") {
            // Remove first occurrence
            const hi = (me?.hand || []).indexOf(chosen);
            const nh = [...(me?.hand || [])]; nh.splice(hi, 1);
            updates.hand = nh;
          } else {
            const ng = { ...me?.grimoire };
            if (ng[chosen] > 1) ng[chosen]--; else delete ng[chosen];
            updates.grimoire = ng;
          }
          updates.necropolis = [...(me?.necropolis || []), chosen];
          await ctx.updatePlayer(ctx.code, ctx.playerIdx, updates);
          ctx.setTargetPicker(null);
          // Step 2: Add from Grimoire
          await searchAndAdd("grimoire", c => c?.type === "Escendant" && c?.level <= maxLvl, 1, label, ctx);
        },
      });
      return;
    }
    // ⚠ THE LUXMOIRE, NOT THE GRIMOIRE. Printed text: "Search your
    // LUXMOIRE for a lvl 2 Impera and add it to your hand." This
    // searched the Grimoire — and Impera do not live in the Grimoire,
    // they live in the Luxmoire, so the pool was ALWAYS empty and the
    // card has been dead in every shipping build: 3 Khao for "No Level
    // 2 Impera available." The NPC's tome book has carried the correct
    // pile (and a note saying so) since it was written; this is the
    // player side catching up. caP'n on all five of these: *"lets fix
    // them"* — the code moves to the card, never the other way.
    if (id === "tome_shadow_blanket") {
      await searchAndAdd("luxmoire", c => c?.type === "Impera" && c?.level === 2, 1, "Level 2 Impera", ctx);
      return;
    }
    if (id === "tome_inverted_knife") {
      await searchAndAdd("grimoire", c => c?.type === "U'hlti", 1, "U'hlti", ctx);
      return;
    }
    // ⚠ SAME BUG, SAME PILE, DIFFERENT CARD. Printed text: "Add 1
    // Impera from your LUXMOIRE and 1 U'hlti from your Grimoire to your
    // hand." The card names both piles by name in one sentence and this
    // branch read the same one twice, so the Impera half never found
    // anything. The U'hlti half was already right and stays as it is.
    if (id === "tome_light_casting_shadow") {
      await searchAndAdd("luxmoire", c => c?.type === "Impera", 1, "Impera", ctx);
      await searchAndAdd("grimoire", c => c?.type === "U'hlti", 1, "U'hlti", ctx);
      return;
    }

    // ── Ritual Tomes ───────────────────────────────────────────
    // ⚠ THE OMNIS RITUALS SAY THREE THINGS AND THIS READ ONE OF THEM.
    // Printed text, all three: "Add 1 Tome (up to lvl N), OR 1 CURIOS,
    // from Necropolis to your hand, EXCEPT A RITUAL TOME." The filter
    // was a bare `type === "Tome" && level <= N`, which got both halves
    // of the clause wrong at once and in opposite directions: it never
    // offered a Curio (a whole limb of the card that has never once
    // been playable), and it happily tutored a RITUAL Tome — so Seal of
    // Omnis could fetch Seal of Omnis, and a Ritual pair in the pile was
    // an engine the card explicitly forbids. The exception rides on the
    // TOME limb only; a Curio's ilk is "wears"/"J'ynxi", never "Ritual",
    // so it is unaffected either way and the clause reads correctly as
    // written. This is `{ anyOf: [{types:["Tome"], maxLevel:N,
    // notIlk:"Ritual"}, {types:["Curio"]}] }` — the exact filter the
    // NPC's tome book has carried since it was written.
    if (id === "tome_seal_of_omnis" || id === "tome_monoliths_of_omnis" || id === "tome_mausoleum_of_omnis") {
      const maxLvl = id === "tome_seal_of_omnis" ? 1 : id === "tome_monoliths_of_omnis" ? 2 : 3;
      await searchAndAdd("necropolis",
        c => (c?.type === "Tome" && c?.level <= maxLvl && c?.ilk !== "Ritual") || c?.type === "Curio",
        1, `Tome (up to L${maxLvl}, no Ritual) or Curios from Necropolis`, ctx);
      return;
    }
    if (id === "tome_seal_of_nihil" || id === "tome_monoliths_of_nihil") {
      const maxLvl = id === "tome_seal_of_nihil" ? 1 : 2;
      await searchAndAdd("necropolis", c => c?.type === "Impera" && c?.level <= maxLvl, 1, `Impera from Necropolis (up to L${maxLvl})`, ctx);
      return;
    }
    // ⚠ MAUSOLEUM OF NIHIL IS NOT THE THIRD RUNG OF THAT LADDER. Seal
    // and Monoliths read "Add 1 Impera, up to lvl N, from Necropolis";
    // Mausoleum reads "Add 1 Impera, up to lvl 3, OR U'HLTI, from
    // Necropolis to your hand." Folding it into the loop cost it the
    // U'hlti limb entirely — and the U'hlti limb carries NO level cap,
    // which is the whole reason the card is the rare of its trio. Split
    // out, matching the book's `{ anyOf: [{types:["Impera"],
    // maxLevel:3}, {types:["U'hlti"]}] }`.
    if (id === "tome_mausoleum_of_nihil") {
      await searchAndAdd("necropolis",
        c => (c?.type === "Impera" && c?.level <= 3) || c?.type === "U'hlti",
        1, "Impera (up to L3) or U'hlti from Necropolis", ctx);
      return;
    }
    // Soulcerer's Seal / Monoliths / Mausoleum — resurrect beings from Necropolis directly to field.
    // Mausoleum special: "Resurrect 1 being up to lvl 3, OR 2 lvl 1 beings."
    if (id === "tome_soulcerers_seal" || id === "tome_soulcerers_monoliths" || id === "tome_soulcerers_mausoleum") {
      const maxLvl = id === "tome_soulcerers_seal" ? 1 : id === "tome_soulcerers_monoliths" ? 2 : 3;
      const resurrectOneFromNecro = async (levelCap) => {
        const nec = me?.necropolis || [];
        // R44 (July 25 2026 wave) — EVERY resurrection method counts
        // toward and respects the per-instance tally, tome effects
        // included. Candidates at their rung's limit don't even show.
        // R85 EXEMPT — Tome effects reach Graveshifted beings; only the
        // paid rite is closed to them. Dedupe by BASE, offer the copy
        // with the most returns left.
        const tomeResTally = me?.resurrectedCards || [];
        const _tomeBest = new Map();
        nec.forEach((nid) => {
          const c = getCard(nid);
          if (c?.type !== "Escendant" || c.level > levelCap) return;
          const used = resTally(tomeResTally, nid);
          if (used >= (RESURRECT_LIMIT[`L${c.level}`] ?? 1)) return;
          const b = baseId(nid);
          const prev = _tomeBest.get(b);
          if (!prev || used < prev.used) _tomeBest.set(b, { nid, c, used });
        });
        const candidates = [..._tomeBest.values()];
        if (candidates.length === 0) { ctx.showToast("No eligible beings in Necropolis.", GM.vellumFaint); return false; }
        const emptySlots = (me?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => !s?.escendant && s?.escling);
        if (emptySlots.length === 0) { ctx.showToast("No empty Escling slots to resurrect onto.", SD.red); return false; }
        return new Promise((resolve) => {
          ctx.setTargetPicker({
            title: `◆ ${card.name} — Choose being to resurrect (up to L${levelCap})`,
            options: candidates.map(({ nid, c }) => ({ id: nid, label: `${c.name} (L${c.level})` })),
            onPick: async (pick) => {
              // Pick destination slot
              ctx.setTargetPicker({
                title: `◆ Resurrect ${getCard(pick.id)?.name} — Choose slot`,
                options: emptySlots.map(({ s, i }) => ({ id: i, label: `Slot ${i + 1} (${getCard(s.escling)?.name || "Bebi"})`, slotIdx: i })),
                onPick: async (slotPick) => {
                  const targetCard = getCard(pick.id);
                  const newField = JSON.parse(JSON.stringify(me?.field || {}));
                  newField.esclings[slotPick.slotIdx] = {
                    ...newField.esclings[slotPick.slotIdx],
                    escendant: pick.id, fallen: false, currentLyf: targetCard.lyf ?? null,
                    attackedThisTurn: false, intangible: false, soul: [], soulUsed: {}, heretical: null,
                  };
                  const newNecro = [...(me?.necropolis || [])];
                  const necroIdx = newNecro.indexOf(pick.id);
                  if (necroIdx >= 0) newNecro.splice(necroIdx, 1);
                  // Live read + conditional write (see handleResurrect stale-anima note).
                  const meLiveE = ctx.matchRef.current?.players?.[ctx.playerIdx] || me;
                  const liveAnimaE = meLiveE?.anima ?? STARTING_ANIMA;
                  const hopeAnima = triggerHopeIfTimeEntry(targetCard, newField, liveAnimaE, ctx);
                  await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: newField, necropolis: newNecro, ...(hopeAnima !== liveAnimaE ? { anima: hopeAnima } : {}), resurrectedCards: [...(meLiveE?.resurrectedCards || []), pick.id] }); // R44 — EVERY method marks the tally
                  ctx.showToast(`✦ ${targetCard.name} Resurrected!`, GM.inkGold);
                  ctx.setTargetPicker(null);
                  resolve(true);
                },
              });
            },
          });
        });
      };
      if (id === "tome_soulcerers_mausoleum") {
        // Choice: 1 L≤3 OR 2 L1
        ctx.setTargetPicker({
          title: "◆ Soulcerer's Mausoleum — Choose mode",
          options: [
            { id: "one", label: "Resurrect 1 being (up to L3)" },
            { id: "two", label: "Resurrect 2 Level 1 beings" },
          ],
          onPick: async (modePick) => {
            ctx.setTargetPicker(null);
            if (modePick.id === "one") {
              await resurrectOneFromNecro(3);
            } else {
              await resurrectOneFromNecro(1);
              // Second resurrect — may have no valid slots left; helper handles gracefully
              await resurrectOneFromNecro(1);
            }
          },
        });
      } else {
        await resurrectOneFromNecro(maxLvl);
      }
      return;
    }

    // ── Magma Carta — OPT: Dim own Anima 4, draw 1. Ruined when opp devotes 4 total ──
    if (id === "tome_magma_carta") {
      const cost = 4;
      // R96 — a voluntary self-dim is still a payment; the old `< cost`
      // let a seat on exactly 4 pay itself to 0 with nothing to collect it.
      if (!canPayVoluntary(me?.anima ?? STARTING_ANIMA, cost)) { ctx.showToast(`Not enough Anima. Need ${animaFare(cost)}.`, SD.red); return; }
      const newAnima = Math.max(0, (me?.anima || STARTING_ANIMA) - cost);
      const pool = Object.entries(me?.grimoire || {}).flatMap(([id, qty]) => Array(qty).fill(id));
      const drawn = pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : null;
      // R100 — the four Anima are the payer's own; SELF ledger.
      const updates = { anima: newAnima, ...buildSelfDimUpdate(me, cost) };
      // ── GRIM/LUX FIX (July 2026): deplete the drawn card — it was
      // being duplicated (hand copy + Grimoire copy).
      if (drawn) {
        const newG = { ...(me?.grimoire || {}) };
        if (newG[drawn] > 1) newG[drawn]--; else delete newG[drawn];
        updates.grimoire = newG;
        updates.hand = [...(me?.hand || []), drawn]; ctx.showToast(`✦ Magma Carta — Dimmed 4 Anima, drew ${getCard(drawn)?.name}!`, SD.gold); }
      else { ctx.showToast("✦ Magma Carta — Dimmed 4 Anima! (Grimoire empty)", SD.gold); }
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, updates);
      return;
    }

    // ── King of the Castle — OPT: Opponent must devote 2 Anima ──
    if (id === "tome_king_of_the_castle") {
      ctx.showToast("◆ King of the Castle is active — opponent must devote 2 Anima on their turn!", SD.gold);
      return;
    }

    // ── Restraining Ordeal — ATV passive, no manual activation ──
    if (id === "tome_restraining_ordeal") {
      ctx.showToast("◆ Restraining Ordeal is active — opponent gains 1 Anima each turn they don't attack!", SD.gold);
      return;
    }

    // ── Contract Tomes ─────────────────────────────────────────
    if (id === "tome_devils_advocate") {
      // Both draw 1 — remove from grimoire when drawn.
      //
      // BUG FIX (Apr 2026): Previously read `me?.hand` directly, which is
      // async-stale from the outer handlePlayTome's splice. That stale hand
      // still contained the Devil's Advocate being played, so the write
      // `[...me.hand, d]` reintroduced it — infinite loop. Filter defensively
      // so even stale reads can't leak the tome back into the hand.
      const myHandClean = (me?.hand || []).filter(hid => hid !== "tome_devils_advocate");
      const myGrim = { ...me?.grimoire };
      const myPool = Object.entries(myGrim).flatMap(([id, qty]) => Array(qty).fill(id));
      if (myPool.length > 0) {
        const d = myPool[Math.floor(Math.random() * myPool.length)];
        if (myGrim[d] > 1) myGrim[d]--; else delete myGrim[d];
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: [...myHandClean, d], grimoire: myGrim });
      }
      const oppGrim = { ...opponent?.grimoire };
      const oppPool = Object.entries(oppGrim).flatMap(([id, qty]) => Array(qty).fill(id));
      if (oppPool.length > 0) {
        const d = oppPool[Math.floor(Math.random() * oppPool.length)];
        if (oppGrim[d] > 1) oppGrim[d]--; else delete oppGrim[d];
        // R10 — draw-then-discard: the adversary's draw resolves at the
        // cap (was blocked outright); their client discards after.
        await ctx.updatePlayer(ctx.code, ctx.oppIdx, { hand: [...(opponent?.hand || []), d], grimoire: oppGrim });
      }
      ctx.showToast("✦ Devil's Advocate — Both Aenarchists drew 1 card!", SD.gold); return;
    }
    if (id === "tome_monkey_suite") {
      const myNew = sealAnimaGain((me?.anima || STARTING_ANIMA), 5);
      const oppNew = sealAnimaGain((opponent?.anima || STARTING_ANIMA), 5);
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { anima: myNew, ...buildAnimaGainUpdate(me, 5) });
      await ctx.updatePlayer(ctx.code, ctx.oppIdx, { anima: oppNew, ...buildAnimaGainUpdate(opponent, 5) });
      ctx.showToast("✦ Monkey Suite — Both Aenarchists gain 5 Anima!", SD.red); return;
    }
    // Seize and Disease — "You and your adversary choose 1 of each other's Tomes to ruin."
    // Each player picks from the OTHER player's tomes. I pick one of opp's to ruin;
    // opp picks one of mine to ruin. Auto-pick on opp's side for single-player feel
    // (the NPC will ruin my most-impactful tome, or random if none stand out).
    if (id === "tome_seize_and_disease") {
      const oppTomes = (opponent?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id);
      const myTomesStanding = (me?.field?.tomes || []).map((t, i) => ({ t, i })).filter(({ t }) => t?.id && t.id !== "tome_seize_and_disease");
      if (oppTomes.length === 0 && myTomesStanding.length === 0) {
        ctx.showToast("No Tomes on either side to ruin.", GM.vellumFaint); return;
      }
      // Step 1: I pick one of opp's tomes (respecting MRF)
      const pickAndRuinOpp = async () => {
        if (oppTomes.length === 0) { ctx.showToast("No opponent Tomes to ruin.", GM.vellumFaint); return; }
        const mrfS = getRequiredTarget(opponent?.field);
        if (mrfS?.kind === "tome") {
          const forced = oppTomes.find(({ i }) => i === mrfS.slotIdx);
          if (forced) {
            const f = JSON.parse(JSON.stringify(opponent?.field || {}));
            const wasRuined = await attemptRuinOppTome(f, forced.i, ctx);
            if (wasRuined) {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), forced.t.id] });
              ctx.showToast(`✦ Seize and Disease — ${getCard(forced.t.id)?.name} ruined!`, SD.red);
            } else {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f });
            }
            return;
          }
        }
        return new Promise((resolve) => {
          ctx.setTargetPicker({
            title: "◆ Seize and Disease — Choose an opponent Tome to ruin",
            options: oppTomes.map(({ t, i }) => ({ id: t.id, label: getCard(t.id)?.name || t.id, tomeIdx: i })),
            onPick: async (opt) => {
              const f = JSON.parse(JSON.stringify(opponent?.field || {}));
              const wasRuined = await attemptRuinOppTome(f, opt.tomeIdx, ctx);
              if (wasRuined) {
                await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f, necropolis: [...(opponent?.necropolis || []), opt.id] });
                ctx.showToast(`✦ ${getCard(opt.id)?.name} ruined!`, SD.red);
              } else {
                await ctx.updatePlayer(ctx.code, ctx.oppIdx, { field: f });
              }
              ctx.setTargetPicker(null);
              resolve();
            },
          });
        });
      };
      // Step 2: Opponent picks one of MINE to ruin (auto-pick for NPC).
      // Prioritize highest-level tome as the most impactful.
      const oppPicksMine = async () => {
        if (myTomesStanding.length === 0) return;
        const ranked = [...myTomesStanding].sort((a, b) => (getCard(b.t.id)?.level || 0) - (getCard(a.t.id)?.level || 0));
        const pick = ranked[0];
        const f = JSON.parse(JSON.stringify(me?.field || {}));
        // ── AUDIT FIX (July 19 2026): soak-aware on MY side too — a
        // standing D'uhmdumn Lord shield (nullifyFirstRuin) soaks the
        // adversary's pick, and my on-ruin clauses fire when it lands.
        const myTome = f.tomes?.[pick.i];
        if (myTome?.nullifyFirstRuin) {
          f.tomes[pick.i] = { ...myTome, nullifyFirstRuin: false };
          await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
          ctx.showToast(`◈ ${getCard(pick.t.id)?.name} — the Lord's shield soaked the adversary's ruin!`, GM.inkGold);
          return;
        }
        f.tomes[pick.i] = null;
        await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f, necropolis: [...(me?.necropolis || []), pick.t.id] });
        ctx.showToast(`◆ Adversary ruined your ${getCard(pick.t.id)?.name}!`, SD.red);
        await applyTomeRuinClauses(ctx.playerIdx, pick.t.id, ctx);
      };
      await pickAndRuinOpp();
      await oppPicksMine();
      return;
    }
    if (id === "tome_solomon_godfry") {
      // BOTH SEATS must ruin 1 of their own beings and its Soul.
      //
      // ⚠ THE PRINTED TEXT ON THIS CARD USES A RETIRED NOUN. src/cards.js
      // calls the two players "Warlocks"; every surface in the shipping
      // game calls them AENARCHISTS (RULEZ_V2 ch.25), and the toast below
      // already does. Quoting the card verbatim here would put the dead
      // word back into this file, so it is named and not reproduced — and
      // the card itself is handed off, because cards.js is the copy a
      // player actually reads.
      //
      // Auto-picks the first standing being on each side (TODO: proper
      // pickers per side). ⭐ Both halves now go through the ONE full
      // being-ruin, with the ward soak OFF: a card that compels its own
      // controller to sacrifice is not an ATTACK on that being, so no ward
      // answers it — and the brain's applySolomonGodfryEffect does not
      // consult the flag either, so this is parity, not invention.
      const myAlive = (me?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      const oppAlive = (opponent?.field?.esclings || []).map((s, i) => ({ s, i })).filter(({ s }) => s?.escendant);
      if (myAlive.length > 0) await ruinBeingAndSoul(ctx.playerIdx, myAlive[0].i, ctx, { soak: false });
      if (oppAlive.length > 0) await ruinBeingAndSoul(ctx.oppIdx, oppAlive[0].i, ctx, { soak: false });
      ctx.showToast("✦ Solomon Godfry's Manslaughter — Both Aenarchists ruined 1 being and its Soul!", SD.red); return;
    }
    // Agree to Disagree — "Your adversary must choose 1 of the following:
    // you draw 1 card and your adversary gains 5 Anima, or you draw 2 cards
    // and they draw 1."
    // The ADVERSARY chooses. For single-player, NPC picks automatically — the
    // rational pick maximizes their side. Option A gives them 5 Anima (safe,
    // defensive). Option B gives both sides card draw (neutral tempo). NPC
    // favors A if low on Anima (<20), else B to deny card advantage.
    if (id === "tome_agree_to_disagree") {
      const oppAnima = opponent?.anima ?? STARTING_ANIMA;
      const optionAisSafer = oppAnima < 20;
      const drawFromGrimoire = async (playerRef, playerIndex, count) => {
        const pool = Object.entries(playerRef?.grimoire || {}).flatMap(([gid, qty]) => Array(qty).fill(gid));
        if (pool.length === 0) return;
        const drawn = [];
        const remainingPool = [...pool];
        for (let i = 0; i < count && remainingPool.length > 0; i++) {
          const idx = Math.floor(Math.random() * remainingPool.length);
          drawn.push(remainingPool.splice(idx, 1)[0]);
        }
        // Rebuild grimoire count map from remaining pool
        const newG = {};
        remainingPool.forEach(gid => { newG[gid] = (newG[gid] || 0) + 1; });
        await ctx.updatePlayer(ctx.code, playerIndex, {
          hand: [...(playerRef?.hand || []), ...drawn],
          grimoire: newG,
        });
      };
      if (optionAisSafer) {
        // Option A: I draw 1, opp gains 5 Anima
        await drawFromGrimoire(me, ctx.playerIdx, 1);
        const newOppAnima = sealAnimaGain(oppAnima, 5);
        await ctx.updatePlayer(ctx.code, ctx.oppIdx, { anima: newOppAnima, ...buildAnimaGainUpdate(opponent, 5) });
        ctx.showToast("✦ Agree to Disagree — Adversary chose: You draw 1, they gain 5 Anima.", SD.gold);
      } else {
        // Option B: I draw 2, opp draws 1
        await drawFromGrimoire(me, ctx.playerIdx, 2);
        await drawFromGrimoire(opponent, ctx.oppIdx, 1);
        ctx.showToast("✦ Agree to Disagree — Adversary chose: You draw 2, they draw 1.", SD.gold);
      }
      return;
    }
    // Judge, Jury, Executioner — "You and your adversary both add 1 Blood
    // Contract to your hand from Necropolis, but yours can also be from your
    // Grimoire." Blood Contract = Contract-ilk Tomes. Opp restricted to
    // Necropolis; I can pick from either.
    if (id === "tome_judge_jury") {
      // Opp side first — auto-pick any Contract Tome from their Necropolis
      const oppNec = opponent?.necropolis || [];
      const oppContracts = oppNec.filter(nid => {
        const c = getCard(nid);
        return c?.type === "Tome" && c?.ilk === "Contract";
      });
      if (oppContracts.length > 0) {
        // Highest-level first (NPC picks best)
        const ranked = [...oppContracts].sort((a, b) => (getCard(b)?.level || 0) - (getCard(a)?.level || 0));
        const chosen = ranked[0];
        const newOppNec = [...oppNec];
        newOppNec.splice(newOppNec.indexOf(chosen), 1);
        await ctx.updatePlayer(ctx.code, ctx.oppIdx, {
          hand: [...(opponent?.hand || []), chosen],
          necropolis: newOppNec,
        });
        ctx.showToast(`◆ Adversary took ${getCard(chosen)?.name} from their Necropolis.`, GM.vellumFaint);
      } else {
        ctx.showToast("◆ Adversary has no Contract Tomes in Necropolis.", GM.vellumFaint);
      }
      // My side — pick from either Grimoire or Necropolis
      const myNecContracts = (me?.necropolis || [])
        .map((nid, i) => ({ nid, src: "necropolis", idx: i, c: getCard(nid) }))
        .filter(({ c }) => c?.type === "Tome" && c?.ilk === "Contract");
      const myGrimoireContracts = Object.entries(me?.grimoire || {})
        .flatMap(([gid, qty]) => {
          const c = getCard(gid);
          if (c?.type === "Tome" && c?.ilk === "Contract") {
            return [{ nid: gid, src: "grimoire", qty, c }];
          }
          return [];
        });
      const myOptions = [
        ...myNecContracts.map(({ nid, src, idx, c }) => ({ id: nid, label: `Necropolis: ${c.name} (L${c.level})`, src, idx })),
        ...myGrimoireContracts.map(({ nid, src, c }) => ({ id: nid, label: `Grimoire: ${c.name} (L${c.level})`, src })),
      ];
      if (myOptions.length === 0) {
        ctx.showToast("No Contract Tomes available in your Grimoire or Necropolis.", GM.vellumFaint); return;
      }
      ctx.setTargetPicker({
        title: "◆ Judge, Jury, Executioner — Choose 1 Blood Contract",
        options: myOptions,
        onPick: async (opt) => {
          const targetCard = getCard(opt.id);
          if (opt.src === "necropolis") {
            const newNec = [...(me?.necropolis || [])];
            newNec.splice(opt.idx, 1);
            await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
              hand: [...(me?.hand || []), opt.id],
              necropolis: newNec,
            });
          } else {
            const newG = { ...(me?.grimoire || {}) };
            newG[opt.id] = (newG[opt.id] || 1) - 1;
            if (newG[opt.id] <= 0) delete newG[opt.id];
            await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
              hand: [...(me?.hand || []), opt.id],
              grimoire: newG,
            });
          }
          ctx.showToast(`✦ ${targetCard.name} added to hand from ${opt.src}.`, SD.gold);
          ctx.setTargetPicker(null);
        },
      });
      return;
    }

    // ── Nihil Tomes ────────────────────────────────────────────
    // Rays of Hope (L1) — Nullify 1 opp card effect this turn; if Omnia, ruin it
    if (id === "tome_rays_of_hope") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.nihilShieldActive = true; f.nihilShieldTurn = ctx.match?.turn || 0;
      f.nihilRuinOmnia = false;
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast("◈ Rays of Hope — Next opponent card effect this turn is nullified!", GM.inkGold); return;
    }
    // Cleansing Burst (L2) — Same + also works on Curios
    if (id === "tome_cleansing_burst") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.nihilShieldActive = true; f.nihilShieldTurn = ctx.match?.turn || 0;
      f.nihilRuinOmnia = true; f.nihilProtectCurios = true;
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast("◈ Cleansing Burst — Next opp card effect nullified; Omnia or Curios attacks also ruined!", GM.inkGold); return;
    }
    // ── Divine Justice (L3) — "for 2 turns", and R103 says what that is
    // caP'n, 2026-08-26: *"if it's just broad '2 turns' that means 2 full
    // turns which consists of 4 × 1/2 turns."* This line read
    // `(match.turn || 0) + 2` — a RAW HALF-TURN offset — so a card that
    // prints two turns of protection delivered one. `durationExpiryTurn`
    // is rules.js's single converter and is the SAME arithmetic
    // cooldownHalfTurns uses, which is the point: a duration and a
    // cooldown can never drift apart again.
    if (id === "tome_divine_justice") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.divineJusticeUntilTurn = durationExpiryTurn(ctx.match?.turn || 0, 2, ctx.match?.seatCount);
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast("◈ Divine Justice — All opponent Omnia Tomes nullified for 2 turns!", GM.inkGold); return;
    }
    // Shower of Lyt (L1) — Beings cannot be attacked by Tomes this turn
    if (id === "tome_shower_of_lyt") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.protectedFromTomesThisTurn = true; f.protectedTomeTurn = ctx.match?.turn || 0;
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast("◈ Shower of Lyt — Beings protected from Tome attacks this turn!", GM.inkGold); return;
    }
    // Radiant Aura (L2) — ward 1 of your cards: next attack on it
    // nullified, no expiry. (C13 canon ruling — see fireShieldTome.
    // Previously set field-level flags nothing read: pure theater.)
    if (id === "tome_radiant_aura") { await fireShieldTome(card, "cards", ctx); return; }
    // ── Phoenix Aura (L3) — "for 2 turns". R103, exactly as Divine
    // Justice above: two FULL turns is four half-turns, not two.
    if (id === "tome_phoenix_aura") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.protectedFromTomesUntilTurn = durationExpiryTurn(ctx.match?.turn || 0, 2, ctx.match?.seatCount);
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast("◈ Phoenix Aura — Cards protected from Tome attacks for 2 turns!", GM.inkGold); return;
    }
    // Wall of Light (L1) — Absorb Anima damage and ADD it this turn
    if (id === "tome_wall_of_light") {
      const f = JSON.parse(JSON.stringify(me?.field || {}));
      f.wallOfLightActive = true; f.wallOfLightTurn = ctx.match?.turn || 0;
      await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
      ctx.showToast("◈ Wall of Light — Anima damage absorbed and added to your Anima this turn!", GM.inkGold); return;
    }
    // Lyt Footed (L2) — ward 1 of your beings: next attack on it
    // nullified, no expiry. (C13 canon ruling — see fireShieldTome.)
    if (id === "tome_lyt_footed") { await fireShieldTome(card, "beings", ctx); return; }
    // Sword & Shield of Nihil (L3) — ward 1 of your cards: next attack
    // nullified, no expiry; the L3 big sibling. (C13 — see fireShieldTome.)
    if (id === "tome_sword_shield_nihil") { await fireShieldTome(card, "cards", ctx); return; }

    // ── Shadow Gambling ────────────────────────────────────────

    if (id === "tome_temptation_manifested") {
      const sgAuto = await consumeSGAutoWin(ctx);
      ctx.setSgModal({ cardName: card.name, description: card.effect, type: "acb", diceCount: 1, autoWin: sgAuto,
        onResolve: async (won, dice) => {
          await recordShadowGamblingOutcome(won, ctx);
          if (won) {
            const pool = me?.necropolis || [];
            if (pool.length === 0) { ctx.showToast("Necropolis is empty!", GM.vellumFaint); return false; }
            ctx.setTargetPicker({ title: "◆ Add any 1 card from Necropolis", cards: pool, count: 1,
              onPick: async (chosen) => { const _c = normPick(chosen);
                // ── AUDIT FIX (July 19 2026): remove ONE Necropolis instance
                // per pick — the old filter wiped ALL copies of a picked id
                // (the GRIM/LUX-class bug; this site was missed in that pass).
                const newNecTM = [...(me?.necropolis || [])];
                _c.forEach(cid => { const ni = newNecTM.indexOf(cid); if (ni !== -1) newNecTM.splice(ni, 1); });
                await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: [...(me?.hand||[]), ..._c], necropolis: newNecTM });
                ctx.setTargetPicker(null);
              }
            });
          } else { ctx.showToast("◇ Adversary wins the Shadow Gambling!", GM.vellumFaint); }
          return won;
        },
        resultText: (r) => r === "win" ? "Add 1 card from your Necropolis to your hand!" : "Your adversary wins this Shadow Gambling."
      }); return;
    }

    if (id === "tome_nevermore") {
      const sgAuto = await consumeSGAutoWin(ctx);
      ctx.setSgModal({ cardName: card.name, description: card.effect, type: "acb", diceCount: 1, autoWin: sgAuto,
        onResolve: async (won) => {
          await recordShadowGamblingOutcome(won, ctx);
          if (won) {
            const pool = Object.keys(me?.grimoire || {});
            if (pool.length === 0) { ctx.showToast("Grimoire is empty!", GM.vellumFaint); return false; }
            ctx.setTargetPicker({ title: "◆ Add any 1 card from Grimoire", cards: pool, count: 1,
              onPick: async (chosen) => { const _c = normPick(chosen);
                // ── GRIM/LUX FIX (July 2026): deplete the tutored copy ──
                const newG = { ...(me?.grimoire || {}) };
                _c.forEach(cid => { if (newG[cid] > 1) newG[cid]--; else if (newG[cid]) delete newG[cid]; });
                await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: [...(me?.hand||[]), ..._c], grimoire: newG });
                ctx.setTargetPicker(null);
              }
            });
          } else { ctx.showToast("◇ Adversary wins the Shadow Gambling!", GM.vellumFaint); }
          return won;
        },
        resultText: (r) => r === "win" ? "Add 1 card from your Grimoire to your hand!" : "Your adversary wins this Shadow Gambling."
      }); return;
    }

    if (id === "tome_snake_eyes") {
      const sgAuto = await consumeSGAutoWin(ctx);
      ctx.setSgModal({ cardName: card.name, description: card.effect, type: "acb", diceCount: 2, autoWin: sgAuto,
        onResolve: async (won) => {
          await recordShadowGamblingOutcome(won, ctx);
          if (won) {
            const pool = Object.keys(me?.grimoire || {});
            ctx.setTargetPicker({ title: "◆ Add any 2 cards from Grimoire", cards: pool, count: 2,
              onPick: async (chosen) => { const _c = normPick(chosen);
                // ── GRIM/LUX FIX (July 2026): deplete the tutored copies ──
                const newG = { ...(me?.grimoire || {}) };
                _c.forEach(cid => { if (newG[cid] > 1) newG[cid]--; else if (newG[cid]) delete newG[cid]; });
                await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: [...(me?.hand||[]), ..._c], grimoire: newG });
                ctx.setTargetPicker(null);
              }
            });
          }
          return won;
        },
        resultText: (r) => r === "win" ? "Both dice matched! Add 2 cards from your Grimoire!" : "No match. No reward."
      }); return;
    }

    if (id === "tome_dragon_deyes") {
      const sgAuto = await consumeSGAutoWin(ctx);
      ctx.setSgModal({ cardName: card.name, description: card.effect, type: "acb", diceCount: 2, autoWin: sgAuto,
        onResolve: async (won, dice, chosen) => {
          // ── AUDIT FIX (July 19 2026): per-die accounting, per card text —
          // "You draw 1 card for every incorrect match; your adversary draws
          // 1 card for every correct match." The old impl was binary (one
          // draw either way) and recorded the streak backwards.
          // Casino Royale's fortune: an auto-win = both dice incorrect.
          const dieArr = Array.isArray(dice) ? dice : [];
          const correctCount = sgAuto ? 0 : dieArr.filter(d => d === chosen).length;
          const incorrectCount = dieArr.length - correctCount;
          // ── CANON (caP'n, July 19 2026): the SG is only WON when the
          // entire effect resolves in the owner's favor — zero correct
          // matches. Any correct match breaks the streak.
          await recordShadowGamblingOutcome(correctCount === 0, ctx);
          // My draws — one per incorrect match, Grimoire depleted.
          if (incorrectCount > 0) {
            const newG = { ...(me?.grimoire || {}) };
            const pool = Object.entries(newG).flatMap(([gid, qty]) => Array(qty).fill(gid));
            const drawn = [];
            for (let i = 0; i < incorrectCount && pool.length > 0; i++) {
              const pi = Math.floor(Math.random() * pool.length);
              const d = pool.splice(pi, 1)[0];
              drawn.push(d);
              if (newG[d] > 1) newG[d]--; else delete newG[d];
            }
            if (drawn.length > 0) {
              await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: [...(me?.hand||[]), ...drawn], grimoire: newG });
              ctx.showToast(`◆ Dragon D'eyes — ${incorrectCount} incorrect: you drew ${drawn.length}!`, SD.gold);
            }
          }
          // Adversary draws — one per correct match (R10 settles the cap).
          if (correctCount > 0) {
            const newG = { ...(opponent?.grimoire || {}) };
            const pool = Object.entries(newG).flatMap(([gid, qty]) => Array(qty).fill(gid));
            // R10 — draw-then-discard: every correct match draws; the cap is
            // settled by a discard afterwards, not by shorting the draw.
            const drawn = [];
            for (let i = 0; i < correctCount && pool.length > 0; i++) {
              const pi = Math.floor(Math.random() * pool.length);
              const d = pool.splice(pi, 1)[0];
              drawn.push(d);
              if (newG[d] > 1) newG[d]--; else delete newG[d];
            }
            if (drawn.length > 0) {
              await ctx.updatePlayer(ctx.code, ctx.oppIdx, { hand: [...(opponent?.hand||[]), ...drawn], grimoire: newG });
              ctx.showToast(`◆ Dragon D'eyes — ${correctCount} correct: adversary drew ${drawn.length}.`, GM.vellumFaint);
            }
          }
          return correctCount === 0;
        },
        resultText: (r) => r === "win" ? "Clean roll — every incorrect match is a card for you!" : "Correct matches feed your adversary."
      }); return;
    }

    if (id === "tome_gambits_lucky_cat") {
      const sgAuto = await consumeSGAutoWin(ctx);
      ctx.setSgModal({ cardName: card.name, description: card.effect, type: "coin", autoWin: sgAuto,
        onResolve: async (won) => {
          await recordShadowGamblingOutcome(won, ctx);
          if (won) {
            const grimPool = Object.keys(me?.grimoire || {});
            const necPool = me?.necropolis || [];
            const all = [...grimPool, ...necPool];
            ctx.setTargetPicker({ title: "◆ Add any 2 cards from Grimoire or Necropolis", cards: all, count: 2,
              onPick: async (chosen) => { const _c = normPick(chosen);
                // ── GRIM/LUX FIX (July 2026): remove ONE Necropolis
                // instance per pick (the old filter wiped ALL copies of a
                // picked id), and DEPLETE the Grimoire when the pick came
                // from there (it was being duplicated).
                const newNec = [...(me?.necropolis || [])];
                const newG = { ...(me?.grimoire || {}) };
                _c.forEach(cid => {
                  const ni = newNec.indexOf(cid);
                  if (ni !== -1) { newNec.splice(ni, 1); return; }
                  if (newG[cid] > 1) newG[cid]--; else if (newG[cid]) delete newG[cid];
                });
                await ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: [...(me?.hand||[]), ..._c], grimoire: newG, necropolis: newNec });
                ctx.setTargetPicker(null);
              }
            });
          } else { ctx.showToast("◇ Adversary wins the Shadow Gambling!", GM.vellumFaint); }
          return won;
        },
        resultText: (r) => r === "win" ? "Lucky cat! Add 2 cards to your hand!" : "Adversary wins the Shadow Gambling."
      }); return;
    }

    if (id === "tome_casino_royale") {
      // Card text: "except Casino Royale" — a banked auto-win never
      // applies to another Casino Royale, so no consumeSGAutoWin here.
      ctx.setSgModal({ cardName: card.name, description: card.effect, type: "coin",
        onResolve: async (won) => {
          await recordShadowGamblingOutcome(won, ctx);
          if (won) {
            const f = JSON.parse(JSON.stringify(me?.field || {}));
            f.nextSGAutoWin = true;
            await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
            ctx.showToast("✦ Casino Royale — Your next Shadow Gambling is an automatic win!", SD.gold);
          }
          return won;
        },
        resultText: (r) => r === "win" ? "✦ Your next Shadow Gambling is an automatic win!" : "The coin was not in your favor."
      }); return;
    }

    if (id === "tome_daft_fantasy") {
      const grimPool = Object.keys(me?.grimoire || {}).filter(c => { const gc = getCard(c); return gc?.level >= 1 && gc?.level <= 3; });
      if (grimPool.length === 0) { ctx.showToast("No eligible cards in Grimoire.", GM.vellumFaint); return; }
      const dfAuto = await consumeSGAutoWin(ctx);
      const chosen = grimPool[Math.floor(Math.random() * grimPool.length)];
      const chosenCard = getCard(chosen);
      ctx.setSgModal({ cardName: card.name, description: `Chosen card: hidden. Adversary guesses the level.`, type: "guess_level", autoWin: dfAuto,
        onResolve: (guessedLevel, autoWin) => {
          // ── CANON (caP'n, July 19 2026): printed text stands, and it is
          // deliberately backwards-feeling. The adversary guesses:
          //   CORRECT → the card is added to the OWNER's hand (owner wins).
          //   WRONG   → the adversary wins the Shadow Gambling.
          // The mind-game: the adversary WANTS to miss. Casino Royale's
          // fortune counts as a correct guess in the owner's favor.
          const correct = autoWin || guessedLevel === chosenCard?.level;
          const won = correct;
          // Fire-and-forget streak record (onResolve here is sync, but the
          // helper is async — the promise runs in background, which is fine).
          recordShadowGamblingOutcome(won, ctx);
          if (correct) {
            // ── GRIM/LUX FIX (July 2026): deplete the won copy ──
            const newG = { ...(me?.grimoire || {}) };
            if (newG[chosen] > 1) newG[chosen]--; else delete newG[chosen];
            ctx.updatePlayer(ctx.code, ctx.playerIdx, { hand: [...(me?.hand||[]), chosen], grimoire: newG });
            ctx.showToast(`✦ Daft Fantasy — Adversary guessed L${chosenCard?.level} right! ${chosenCard?.name} added to your hand!`, SD.gold);
          } else {
            ctx.showToast(`◇ Daft Fantasy — Adversary guessed L${guessedLevel}, missed on purpose or not — they win the Shadow Gambling.`, GM.vellumFaint);
          }
          return won;
        },
        resultText: (r) => r === "win" ? `✦ Correct guess! ${chosenCard?.name} added to your hand!` : "Wrong guess — adversary wins the Shadow Gambling."
      }); return;
    }

    if (id === "tome_underdogs_howl") {
      const grimPool = Object.keys(me?.grimoire || {});
      if (grimPool.length === 0) { ctx.showToast("No cards in Grimoire to bet.", GM.vellumFaint); return; }
      ctx.setTargetPicker({ title: "◆ Underdog's Howl — Pick 1 card to bet", cards: grimPool, count: 1,
        onPick: async (betCards) => {
          // ⚠ THIS STAKED THE FIRST *CHARACTER* OF A CARD ID (2026-09-14).
          // A `cards:` picker hands onPick the RAW element, and for a
          // Grimoire pool that element is a STRING — so `betCards[0]` was
          // "t", not "tome_regnant". `f.pendingBet = "t"` then settled in
          // recordShadowGamblingOutcome, which wrote "t" into the hand (or
          // the Necropolis) while the Grimoire decrement no-opped: a card
          // that does not exist minted out of nowhere, the real staked card
          // never staked, and the toast naming `undefined`. `normPick` has
          // existed in this file for exactly this shape since the lift and
          // every other `cards:` branch already calls it — this was the one
          // that did not.
          const betCardId = normPick(betCards)[0];
          if (!betCardId || !getCard(betCardId)) {
            ctx.showToast("That is not a card that can be bet.", GM.vellumFaint);
            ctx.setTargetPicker(null); return;
          }
          const f = JSON.parse(JSON.stringify(me?.field || {}));
          f.pendingBet = betCardId;
          await ctx.updatePlayer(ctx.code, ctx.playerIdx, { field: f });
          ctx.showToast(`✦ ${getCard(betCardId)?.name} is up for bet! Win your next Shadow Gambling to claim it.`, SD.gold);
          ctx.setTargetPicker(null);
        }
      }); return;
    }

    // ── GOODBYE HORSES — TWO TOMES, TWO GUESSES, ONE GAMBLE ───────
    // Printed text: *"Pick 2 Tomes from your Grimoire, your adversary
    // guesses what Ilk they each are, you keep all wrong choices."*
    // THREE THINGS WERE WRONG AT ONCE and each one of them cut the card in
    // half (2026-09-14):
    //
    //  1. HALF THE CARD NEVER RESOLVED. The picker closed on the FIRST
    //     onPick, so Tome 2 was never guessed and never awarded — an
    //     8-Khao rare that delivered one of its two Tomes. "all wrong
    //     choices" is plural and the brain (npcTomeBook's goodbyeHorses
    //     step) has always resolved both.
    //  2. THE POOL WAS A SET. `Object.keys(grimoire)` counts distinct ids,
    //     so a Grimoire holding three copies of ONE Tome read as length 1
    //     and the card refused to play at all — while the brain's own
    //     want() counts COPIES (poolCount sums qty) and fires happily.
    //     "2 Tomes" is two CARDS. Conservation and eligibility are both
    //     multiset questions; this is the trap, on the eligibility side.
    //  3. THE STREAK MOVED TWICE. recordShadowGamblingOutcome fired per
    //     guess, so one card could break the streak and rebuild it. This
    //     is ONE Shadow Gambling; it records once, on whether anything was
    //     kept — which is exactly what the brain's recordNpcSG does.
    //
    // Each award also RE-READS the seat. Two sequential wins off one stale
    // `me.grimoire` clone made the second write clobber the first's
    // decrement, minting a copy (THE REBASE LAW).
    if (id === "tome_goodbye_horses") {
      const grimBag = Object.entries(me?.grimoire || {})
        .flatMap(([gid, qty]) => getCard(gid)?.type === "Tome" ? Array(Math.max(0, qty)).fill(gid) : []);
      if (grimBag.length < 2) { ctx.showToast("Need at least 2 Tomes in Grimoire.", GM.vellumFaint); return; }
      const picked = [...grimBag].sort(() => Math.random() - 0.5).slice(0, 2);
      const pickedCards = picked.map(getCard);
      const ILKS8 = ["Construction","Treasure","Nihil","Omnia","Ancient Sorcery","Ritual","Shadow Gambling","Contract"];
      let kept = 0;
      const askGuess = (n) => {
        ctx.setTargetPicker({
          title: `◆ Goodbye Horses — Adversary guesses the Ilk of Tome ${n + 1} of 2`,
          options: ILKS8.map(ilk => ({ id: `t${n + 1}_${ilk}`, label: `Tome ${n + 1}: ${ilk}`, ilk, tomeNum: n })),
          onPick: async (opt) => {
            const guessedIlk = opt.ilk;
            const actual = pickedCards[n]?.ilk;
            const iWin = guessedIlk !== actual;   // a WRONG guess keeps the Tome
            if (iWin) {
              // Fresh read per award — see THE REBASE LAW note above.
              const liveGH = livePlayer(ctx.playerIdx, ctx.me, ctx);
              const wonTome = picked[n];
              const newG = { ...(liveGH?.grimoire || {}) };
              if ((newG[wonTome] || 0) > 0) {
                if (newG[wonTome] > 1) newG[wonTome]--; else delete newG[wonTome];
                await ctx.updatePlayer(ctx.code, ctx.playerIdx, {
                  hand: [...(liveGH?.hand || []), wonTome], grimoire: newG,
                });
                kept += 1;
                ctx.showToast(`✦ Wrong! ${pickedCards[n]?.name} (${actual}) added to your hand!`, SD.gold);
              } else {
                ctx.showToast(`◇ ${pickedCards[n]?.name} is no longer in your Grimoire.`, GM.vellumFaint);
              }
            } else {
              ctx.showToast(`◇ Correct guess for Tome ${n + 1}! Adversary wins that one.`, GM.vellumFaint);
            }
            if (n + 1 < picked.length) { askGuess(n + 1); return; }
            // Both guessed — ONE Shadow Gambling, recorded once.
            await recordShadowGamblingOutcome(kept > 0, ctx);
            ctx.setTargetPicker(null);
          },
        });
      };
      ctx.showToast(`◆ Goodbye Horses — Adversary must guess the Ilk of 2 Tomes. Tap each guess.`, SD.gold);
      askGuess(0);
      return;
    }

    // Fallback — show effect text
    ctx.showToast(`${card.effectTag ? card.effectTag + ": " : ""}${card.effect?.substring(0, 80) || ""}`, GM.inkGold);
  };
